import { Application, Container, Sprite, Texture, Ticker } from "pixi.js";
import {
  itemTextureKeyForType,
  keyTextureKeyForTile,
  keychainUrls,
  lampTextureKeyForTile,
  PlayerAnimationState,
  PlayerDirection,
  TextureAtlas,
  textureKeyForTile,
  uiIconUrls,
} from "./assets";
import { AudioController, FootstepSurface, LowFuelWarningConfig, MiningSurface, MusicKey } from "./audio";
import { InputState } from "./input";
import { isSolidTile, isWallTile, LevelData, LevelEntity, tileAt } from "./level";
import balanceConfig from "./config/game-balance.json";

type Vec2 = {
  x: number;
  y: number;
};

type EntitySprite = {
  entity: LevelEntity;
  sprite: Sprite;
  kind: "key" | "door" | "dealer" | "dealerWagon" | "item" | "light" | "fuelStation";
  width: number;
  height: number;
  offsetX: number;
  offsetY: number;
};

type InventorySlot = {
  type: string;
  count: number;
};

type MiningJob = {
  x: number;
  y: number;
  tile: string;
  slotIndex: number;
  startedAt: number;
};

type InventoryItemType = "range" | "sonar" | "fuelStation" | "pickaxe" | "compass";
type PowerUpType = "betterTank" | "advancedView" | "gemMagnet";
type ShopItemType = InventoryItemType | PowerUpType | "easterEgg";
type ShopOffer = {
  type: ShopItemType;
  label: string;
  price: number;
  kind: "item" | "powerup" | "special";
  description: string;
};

export type RunEndStatus = "victory" | "gameOver";
export type LightingMode = "classic" | "occlusion";

export type LabyrinthOptions = {
  onRunEnded?: (status: RunEndStatus, debugState: unknown) => void;
  audio?: AudioController;
  lightingMode?: LightingMode;
};

const PLAYER_HITBOX_HALF_WIDTH = 18;
const PLAYER_HITBOX_TOP = 18;
const PLAYER_HITBOX_BOTTOM = 27;
const PLAYER_ENTITY_RADIUS = 20;
const VIEW_PADDING_TILES = 3;
const PLAYER_ANIMATION_FPS = 10;
const PLAYER_IDLE_ANIMATION_FPS = 2.2;
const INVENTORY_ORDER: InventoryItemType[] = ["range", "sonar", "fuelStation", "pickaxe", "compass"];
const POWERUP_ORDER: PowerUpType[] = ["betterTank", "advancedView", "gemMagnet"];
const WALL_BLEED_LEFT = 1;
const WALL_BLEED_RIGHT = 1 << 1;
const WALL_BLEED_TOP = 1 << 2;
const WALL_BLEED_BOTTOM = 1 << 3;
const LAMP_NEIGHBOR_WALL_BLEED_RATIO = 0.5;
const LIGHT_FLOOD_STEPS = [
  { dx: 1, dy: 0, wallSideMask: WALL_BLEED_LEFT },
  { dx: -1, dy: 0, wallSideMask: WALL_BLEED_RIGHT },
  { dx: 0, dy: 1, wallSideMask: WALL_BLEED_TOP },
  { dx: 0, dy: -1, wallSideMask: WALL_BLEED_BOTTOM },
] as const;
type ExternalLightEmitter = {
  position: Vec2;
  normal: Vec2 | null;
};
type LightOcclusionKind = "player" | "lamp" | "external";
const INVENTORY_SLOT_POSITIONS = [
  { x: 49, y: 59 },
  { x: 106, y: 59 },
  { x: 167, y: 59 },
  { x: 228, y: 59 },
  { x: 286, y: 59 },
  { x: 348, y: 59 },
] as const;
const SHOP_CATALOG: Record<ShopItemType, ShopOffer> = {
  sonar: {
    type: "sonar",
    label: "Arcane Eye",
    price: 3,
    kind: "item",
    description: "Zeigt kurz ein blockiges Sonarbild der Karte.",
  },
  fuelStation: {
    type: "fuelStation",
    label: "Camp Fire",
    price: 3,
    kind: "item",
    description: "Platziert ein Lagerfeuer, das Licht-Fuel nachfüllt.",
  },
  range: {
    type: "range",
    label: "Zoom Scroll",
    price: 1,
    kind: "item",
    description: "Zoomt die Kamera für wenige Sekunden heraus.",
  },
  pickaxe: {
    type: "pickaxe",
    label: "Pickaxe",
    price: 15,
    kind: "item",
    description: "Baut eine Wand direkt vor dir ab.",
  },
  compass: {
    type: "compass",
    label: "Compass",
    price: 10,
    kind: "item",
    description: "Zeigt kurz zum nächsten Schlüssel oder Ausgang.",
  },
  betterTank: {
    type: "betterTank",
    label: "Better Tank",
    price: 15,
    kind: "powerup",
    description: "Reduziert den Fuel-Verbrauch dauerhaft.",
  },
  advancedView: {
    type: "advancedView",
    label: "Advanced View",
    price: 10,
    kind: "powerup",
    description: "Erweitert den maximalen Lichtbereich dauerhaft.",
  },
  gemMagnet: {
    type: "gemMagnet",
    label: "Gem Magnet",
    price: 20,
    kind: "powerup",
    description: "Sammelt Gems in größerer Reichweite ein.",
  },
  easterEgg: {
    type: "easterEgg",
    label: "Golden Egg",
    price: 1,
    kind: "special",
    description: "Geheimes Relikt. Öffnet einen QR-Code zum Pixel-Prototyp.",
  },
};
const SHOP_ASSORTMENTS: Array<ShopItemType[]> = [
  ["sonar", "range", "fuelStation", "pickaxe", "compass", "betterTank"],
  ["sonar", "range", "fuelStation", "compass", "gemMagnet", "advancedView"],
  ["sonar", "range", "fuelStation", "easterEgg", "betterTank", "pickaxe"],
];
const EASTER_EGG_LINK = "https://wokwi.com/projects/343969264097559122";
type RuntimeConfig = typeof balanceConfig.runtime;

const BASE_RUNTIME: RuntimeConfig = deepMergeRuntime(balanceConfig.runtime, {});
let RANGE_DURATION_MS = BASE_RUNTIME.durations_ms.range;
let COMPASS_DURATION_MS = BASE_RUNTIME.durations_ms.compass;
let SONAR_DURATION_MS = BASE_RUNTIME.durations_ms.sonar;
let DOOR_ANIMATION_MS = BASE_RUNTIME.durations_ms.door_animation;
let MINING_ANIMATION_MS = BASE_RUNTIME.durations_ms.mining_animation;
let BASE_PLAYER_LIGHT_RADIUS = BASE_RUNTIME.player_light.base_radius;
let ADVANCED_PLAYER_LIGHT_RADIUS = BASE_RUNTIME.player_light.advanced_view_radius;
let PLAYER_LIGHT_FUEL_CURVE_EXPONENT = BASE_RUNTIME.player_light.fuel_curve_exponent;
let PLAYER_LIGHT_CORE_RATIO_BASE = BASE_RUNTIME.player_light.core_ratio_base;
let PLAYER_LIGHT_CORE_RATIO_SCALE = BASE_RUNTIME.player_light.core_ratio_scale;
let PLAYER_LIGHT_CORE_RATIO_MIN = BASE_RUNTIME.player_light.core_ratio_min;
let PLAYER_LIGHT_CORE_RATIO_MAX = BASE_RUNTIME.player_light.core_ratio_max;
let RANGE_ZOOM = BASE_RUNTIME.camera.range_zoom;
let ADVANCED_VIEW_ZOOM_FACTOR = BASE_RUNTIME.camera.advanced_view_zoom_factor;
let DEALER_LOOK_DISTANCE_TILES = BASE_RUNTIME.dealer.look_distance_tiles;
let DEALER_INTERACTION_DISTANCE_TILES = BASE_RUNTIME.dealer.interaction_distance_tiles;
let DEALER_BUBBLE_CYCLE_MS = BASE_RUNTIME.dealer.bubble_cycle_ms;
let REFILL_SOUND_DELAY_MS = BASE_RUNTIME.refill.sound_delay_ms;
let REFILL_DISTANCE_TILES = BASE_RUNTIME.refill.distance_tiles;
let REFILL_RATE_PER_SECOND = BASE_RUNTIME.refill.rate_per_second;
let FUEL_PICKUP_AMOUNT = BASE_RUNTIME.pickup.fuel_amount;
let PICKUP_DISTANCE_TILES = BASE_RUNTIME.pickup.default_distance_tiles;
let COIN_MAGNET_DISTANCE_TILES = BASE_RUNTIME.pickup.coin_magnet_distance_tiles;
let COIN_MAGNET_PULL_MIN = BASE_RUNTIME.pickup.coin_magnet_pull_min;
let COIN_MAGNET_PULL_MAX = BASE_RUNTIME.pickup.coin_magnet_pull_max;
let COIN_MAGNET_PULL_BIAS = BASE_RUNTIME.pickup.coin_magnet_pull_bias;
let LAMP_LIT_ZONE_FACTOR = BASE_RUNTIME.light_sources.lamp_lit_zone_factor;
let LAMP_LIGHT_CORE_RATIO = BASE_RUNTIME.light_sources.lamp_core_ratio;
let NON_PLAYER_LIGHT_OCCLUSION_ENABLED = BASE_RUNTIME.light_sources.occlusion_non_player_enabled;
let PLAYER_LIGHT_OCCLUSION_ENABLED = BASE_RUNTIME.light_sources.occlusion_apply_to_player;
let NON_PLAYER_LIGHT_WALL_BLEED_RATIO = BASE_RUNTIME.light_sources.occlusion_wall_bleed_ratio;
let FUEL_STATION_LIGHT_RADIUS = BASE_RUNTIME.light_sources.fuel_station_radius;
let FUEL_STATION_LIGHT_CORE_RATIO = BASE_RUNTIME.light_sources.fuel_station_core_ratio;
let DEALER_LIGHT_RADIUS = BASE_RUNTIME.light_sources.dealer_radius;
let DEALER_LIGHT_CORE_RATIO = BASE_RUNTIME.light_sources.dealer_core_ratio;
let DEALER_LIT_ZONE_RADIUS = BASE_RUNTIME.light_sources.dealer_lit_zone_radius;
let KEY_LIGHT_RADIUS = BASE_RUNTIME.light_sources.key_radius;
let KEY_LIGHT_CORE_RATIO = BASE_RUNTIME.light_sources.key_core_ratio;
let FUEL_WAVE_SEGMENTS = BASE_RUNTIME.fuel_wave.segments;
let FUEL_WAVE_SPEED = BASE_RUNTIME.fuel_wave.speed;
let FUEL_WAVE_LENGTH_PX = BASE_RUNTIME.fuel_wave.length_px;
let FUEL_WAVE_AMPLITUDE_RATIO = BASE_RUNTIME.fuel_wave.amplitude_ratio;
let FUEL_WAVE_VISUAL_DEADZONE = BASE_RUNTIME.fuel_wave.visual_deadzone;
let FUEL_WAVE_VISUAL_CURVE = BASE_RUNTIME.fuel_wave.visual_curve;
let LOW_FUEL_WARNING: LowFuelWarningConfig = {
  threshold: BASE_RUNTIME.low_fuel_warning.threshold,
  minVolume: BASE_RUNTIME.low_fuel_warning.min_volume,
  maxVolume: BASE_RUNTIME.low_fuel_warning.max_volume,
  maxPlaybackRate: BASE_RUNTIME.low_fuel_warning.max_playback_rate,
};

function applyRuntimeForLevel(level: LevelData, lightingMode: LightingMode): void {
  const runtime = resolveRuntimeForLevel(level);
  RANGE_DURATION_MS = runtime.durations_ms.range;
  COMPASS_DURATION_MS = runtime.durations_ms.compass;
  SONAR_DURATION_MS = runtime.durations_ms.sonar;
  DOOR_ANIMATION_MS = runtime.durations_ms.door_animation;
  MINING_ANIMATION_MS = runtime.durations_ms.mining_animation;
  BASE_PLAYER_LIGHT_RADIUS = runtime.player_light.base_radius;
  ADVANCED_PLAYER_LIGHT_RADIUS = runtime.player_light.advanced_view_radius;
  PLAYER_LIGHT_FUEL_CURVE_EXPONENT = runtime.player_light.fuel_curve_exponent;
  PLAYER_LIGHT_CORE_RATIO_BASE = runtime.player_light.core_ratio_base;
  PLAYER_LIGHT_CORE_RATIO_SCALE = runtime.player_light.core_ratio_scale;
  PLAYER_LIGHT_CORE_RATIO_MIN = runtime.player_light.core_ratio_min;
  PLAYER_LIGHT_CORE_RATIO_MAX = runtime.player_light.core_ratio_max;
  RANGE_ZOOM = runtime.camera.range_zoom;
  ADVANCED_VIEW_ZOOM_FACTOR = runtime.camera.advanced_view_zoom_factor;
  DEALER_LOOK_DISTANCE_TILES = runtime.dealer.look_distance_tiles;
  DEALER_INTERACTION_DISTANCE_TILES = runtime.dealer.interaction_distance_tiles;
  DEALER_BUBBLE_CYCLE_MS = runtime.dealer.bubble_cycle_ms;
  REFILL_SOUND_DELAY_MS = runtime.refill.sound_delay_ms;
  REFILL_DISTANCE_TILES = runtime.refill.distance_tiles;
  REFILL_RATE_PER_SECOND = runtime.refill.rate_per_second;
  FUEL_PICKUP_AMOUNT = runtime.pickup.fuel_amount;
  PICKUP_DISTANCE_TILES = runtime.pickup.default_distance_tiles;
  COIN_MAGNET_DISTANCE_TILES = runtime.pickup.coin_magnet_distance_tiles;
  COIN_MAGNET_PULL_MIN = runtime.pickup.coin_magnet_pull_min;
  COIN_MAGNET_PULL_MAX = runtime.pickup.coin_magnet_pull_max;
  COIN_MAGNET_PULL_BIAS = runtime.pickup.coin_magnet_pull_bias;
  LAMP_LIT_ZONE_FACTOR = runtime.light_sources.lamp_lit_zone_factor;
  LAMP_LIGHT_CORE_RATIO = runtime.light_sources.lamp_core_ratio;
  NON_PLAYER_LIGHT_OCCLUSION_ENABLED = runtime.light_sources.occlusion_non_player_enabled;
  PLAYER_LIGHT_OCCLUSION_ENABLED = runtime.light_sources.occlusion_apply_to_player;
  NON_PLAYER_LIGHT_WALL_BLEED_RATIO = runtime.light_sources.occlusion_wall_bleed_ratio;
  FUEL_STATION_LIGHT_RADIUS = runtime.light_sources.fuel_station_radius;
  FUEL_STATION_LIGHT_CORE_RATIO = runtime.light_sources.fuel_station_core_ratio;
  DEALER_LIGHT_RADIUS = runtime.light_sources.dealer_radius;
  DEALER_LIGHT_CORE_RATIO = runtime.light_sources.dealer_core_ratio;
  DEALER_LIT_ZONE_RADIUS = runtime.light_sources.dealer_lit_zone_radius;
  KEY_LIGHT_RADIUS = runtime.light_sources.key_radius;
  KEY_LIGHT_CORE_RATIO = runtime.light_sources.key_core_ratio;
  FUEL_WAVE_SEGMENTS = runtime.fuel_wave.segments;
  FUEL_WAVE_SPEED = runtime.fuel_wave.speed;
  FUEL_WAVE_LENGTH_PX = runtime.fuel_wave.length_px;
  FUEL_WAVE_AMPLITUDE_RATIO = runtime.fuel_wave.amplitude_ratio;
  FUEL_WAVE_VISUAL_DEADZONE = runtime.fuel_wave.visual_deadzone;
  FUEL_WAVE_VISUAL_CURVE = runtime.fuel_wave.visual_curve;
  LOW_FUEL_WARNING = {
    threshold: runtime.low_fuel_warning.threshold,
    minVolume: runtime.low_fuel_warning.min_volume,
    maxVolume: runtime.low_fuel_warning.max_volume,
    maxPlaybackRate: runtime.low_fuel_warning.max_playback_rate,
  };
  if (lightingMode === "classic") {
    NON_PLAYER_LIGHT_OCCLUSION_ENABLED = false;
    PLAYER_LIGHT_OCCLUSION_ENABLED = false;
  } else if (lightingMode === "occlusion") {
    NON_PLAYER_LIGHT_OCCLUSION_ENABLED = true;
    PLAYER_LIGHT_OCCLUSION_ENABLED = true;
  }
}

function resolveRuntimeForLevel(level: LevelData): RuntimeConfig {
  const fromLevel = level.difficulty.runtime;
  if (fromLevel && typeof fromLevel === "object") {
    return deepMergeRuntime(BASE_RUNTIME, fromLevel as Record<string, unknown>);
  }
  const overrides = runtimeOverridesForDifficulty(level.difficulty.key);
  return deepMergeRuntime(BASE_RUNTIME, overrides);
}

function runtimeOverridesForDifficulty(key: string): Record<string, unknown> {
  const difficulties = balanceConfig.difficulties as Record<string, unknown>;
  const difficulty = difficulties?.[key];
  if (!difficulty || typeof difficulty !== "object") return {};
  const overrides = (difficulty as Record<string, unknown>).runtime_overrides;
  return overrides && typeof overrides === "object" ? (overrides as Record<string, unknown>) : {};
}

function deepMergeRuntime(base: RuntimeConfig, overrides: Record<string, unknown>): RuntimeConfig {
  return mergeRuntimeObjects(base, overrides) as RuntimeConfig;
}

function mergeRuntimeObjects(base: unknown, overrides: unknown): unknown {
  if (!isPlainRecord(base)) return overrides === undefined ? base : overrides;
  if (!isPlainRecord(overrides)) return base;
  const merged: Record<string, unknown> = { ...base };
  for (const [key, value] of Object.entries(overrides)) {
    if (isPlainRecord(value) && isPlainRecord(merged[key])) {
      merged[key] = mergeRuntimeObjects(merged[key], value);
    } else {
      merged[key] = value;
    }
  }
  return merged;
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

export class LabyrinthPrototype {
  private readonly app: Application;
  private readonly level: LevelData;
  private readonly textures: TextureAtlas;
  private readonly options: LabyrinthOptions;
  private readonly lightingMode: LightingMode;
  private readonly input = new InputState();
  private readonly worldLayer = new Container();
  private readonly entityLayer = new Container();
  private readonly overlayLayer = new Container();
  private readonly player: Sprite;
  private readonly lightCanvas = document.querySelector<HTMLCanvasElement>("#light-mask") ?? document.createElement("canvas");
  private readonly lightContext: CanvasRenderingContext2D;
  private readonly hudMetaElement = document.querySelector<HTMLSpanElement>("#hud-meta");
  private readonly hudStateElement = document.querySelector<HTMLDivElement>("#hud-state");
  private readonly hudFpsElement = document.querySelector<HTMLSpanElement>("#hud-fps");
  private readonly timerElement = document.querySelector<HTMLSpanElement>("#timer-counter");
  private readonly gemCountElement = document.querySelector<HTMLSpanElement>("#gem-count");
  private readonly fuelTankElement = document.querySelector<HTMLDivElement>("#fuel-tank");
  private readonly fuelTankFillElement = document.querySelector<HTMLDivElement>("#fuel-tank-fill");
  private readonly keychainImage = document.querySelector<HTMLImageElement>("#keychain-image");
  private readonly inventoryBarElement = document.querySelector<HTMLDivElement>("#inventory-bar");
  private readonly powerupBarElement = document.querySelector<HTMLDivElement>("#powerup-bar");
  private readonly compassWidget = document.querySelector<HTMLDivElement>("#compass-widget");
  private readonly compassNeedle = document.querySelector<HTMLImageElement>("#compass-needle");
  private readonly actionHintElement = document.querySelector<HTMLDivElement>("#action-hint");
  private readonly sonarOverlay = document.querySelector<HTMLDivElement>("#sonar-overlay");
  private readonly sonarCanvas = document.querySelector<HTMLCanvasElement>("#sonar-map");
  private readonly sonarContext: CanvasRenderingContext2D | null = null;
  private readonly shopOverlay = document.querySelector<HTMLDivElement>("#shop-overlay");
  private readonly shopGridElement = document.querySelector<HTMLDivElement>("#shop-grid");
  private readonly shopGemsElement = document.querySelector<HTMLElement>("#shop-gems");
  private readonly shopCloseButton = document.querySelector<HTMLButtonElement>("#shop-close");
  private readonly shopStatusElement = document.querySelector<HTMLParagraphElement>("#shop-status");
  private readonly easterEggOverlay = document.querySelector<HTMLDivElement>("#easter-egg-overlay");
  private readonly easterEggLink = document.querySelector<HTMLAnchorElement>("#easter-egg-link");
  private readonly easterEggCloseButton = document.querySelector<HTMLButtonElement>("#easter-egg-close");
  private readonly refillIndicator = document.querySelector<HTMLDivElement>("#refill-indicator");
  private readonly dealerBubble = document.querySelector<HTMLDivElement>("#dealer-bubble");
  private readonly tileSprites: Sprite[] = [];
  private readonly entitySprites: EntitySprite[] = [];
  private readonly miningSprite: Sprite;
  private readonly ownedKeys = new Set<number>();
  private readonly inventoryCounts = new Map<string, number>();
  private readonly inventorySlots: Array<InventorySlot | null> = Array.from({ length: INVENTORY_SLOT_POSITIONS.length }, () => null);
  private readonly activePowerUps = new Set<PowerUpType>();
  private readonly fuelStations: LevelEntity[] = [];
  private readonly minedWalls = new Set<string>();
  private readonly collectedKeyEntities = new Set<string>();
  private readonly collectedItemEntities = new Set<string>();
  private readonly openedDoors = new Set<string>();
  private readonly doorOpenedAt = new Map<string, number>();
  private readonly hiddenLights = new Set<string>();
  private readonly magnetizedCoins = new Map<string, Vec2>();
  private readonly camera: Vec2 & { zoom: number } = { x: 0, y: 0, zoom: 1 };
  private readonly playerPos: Vec2;
  private lastPoolCols = 0;
  private lastPoolRows = 0;
  private fpsElapsed = 0;
  private fpsFrames = 0;
  private lastHudStateText = "";
  private lastInventorySignature = "";
  private lastKeychainIndex = -1;
  private lastPowerupSignature = "";
  private lastActionHintText = "";
  private lastSonarSignature = "";
  private lastTimerText = "";
  private readonly itemCooldowns = new Map<string, { startedAt: number; durationMs: number }>();
  private refillingSince = 0;
  private lockedDoorTouchId: string | null = null;
  private lockedDoorPlayedForTouch = false;
  private miningJob: MiningJob | null = null;
  private noClip = false;
  private fuelFrozen = false;
  private dealerTipIndex = -1;
  private dealerTipAt = 0;
  private facing: PlayerDirection = "down";
  private playerAnimationState: PlayerAnimationState = "idle";
  private playerAnimationFrame = 0;
  private playerAnimationElapsed = 0;
  private rangeUntil = 0;
  private sonarUntil = 0;
  private pickaxeArmedUntil = 0;
  private compassUntil = 0;
  private fuelDrainMultiplier = 1;
  private shopOpen = false;
  private easterEggOpen = false;
  private easterEggUnlocked = false;
  private shopDealer: LevelEntity | null = null;
  private coins = 0;
  private fuel = 1;
  private runElapsedMs = 0;
  private paused = false;
  private won = false;
  private gameOver = false;
  private runEndNotified = false;
  private fuelWavePhase = 0;
  private lastFuelWaveClip = "";
  private activeMusicRegion: number | null = null;
  private activeMusicKey: MusicKey | null = null;
  private lastShopSelectionSoundFor = "";

  constructor(app: Application, level: LevelData, textures: TextureAtlas, options: LabyrinthOptions = {}) {
    this.app = app;
    this.level = level;
    this.textures = textures;
    this.options = options;
    this.lightingMode = options.lightingMode ?? "classic";
    applyRuntimeForLevel(level, this.lightingMode);

    const start = level.entities.start ?? { x: 1, y: 1 };
    this.playerPos = {
      x: (start.x + 0.5) * level.tileSize,
      y: (start.y + 0.5) * level.tileSize,
    };

    this.player = new Sprite(this.currentPlayerTexture());
    this.player.anchor.set(0.5);
    this.player.scale.set(1.35);
    this.miningSprite = new Sprite(this.textures.miningFrames[0] ?? Texture.EMPTY);
    this.miningSprite.anchor.set(0.5);
    this.miningSprite.visible = false;

    const lightContext = this.lightCanvas.getContext("2d");
    if (!lightContext) {
      throw new Error("Could not initialize light mask canvas.");
    }
    this.lightContext = lightContext;
    this.sonarContext = this.sonarCanvas?.getContext("2d") ?? null;
    if (this.easterEggLink) {
      this.easterEggLink.href = EASTER_EGG_LINK;
    }
    if (!this.lightCanvas.isConnected) {
      document.body.appendChild(this.lightCanvas);
    }

    this.app.stage.addChild(this.worldLayer);
    this.app.stage.addChild(this.entityLayer);
    this.app.stage.addChild(this.overlayLayer);
    this.overlayLayer.addChild(this.player);
    this.entityLayer.addChild(this.miningSprite);

    this.createEntitySprites();
    this.placeFuelStationAtTile(start.x, start.y);
    this.updateAreaMusic();
    this.input.attach();
    window.addEventListener("keydown", this.onGameKeyDown, { capture: true });
    this.app.canvas.addEventListener("pointerdown", this.onPointerDown);
    this.inventoryBarElement?.addEventListener("click", this.onInventoryClick);
    this.shopGridElement?.addEventListener("click", this.onShopClick);
    this.shopOverlay?.addEventListener("pointerdown", this.onShopOverlayPointerDown);
    this.shopCloseButton?.addEventListener("click", this.onShopCloseClick);
    this.easterEggOverlay?.addEventListener("pointerdown", this.onEasterEggOverlayPointerDown);
    this.easterEggCloseButton?.addEventListener("click", this.onEasterEggCloseClick);
    this.easterEggLink?.addEventListener("click", this.onEasterEggLinkClick);
    this.app.ticker.add(this.tick);
    window.addEventListener("resize", this.onResize);
    this.rebuildTilePool();
    this.updateHudMeta();
    this.updateHudState();
    this.updateTimerDisplay();
  }

  destroy(): void {
    this.input.destroy();
    window.removeEventListener("keydown", this.onGameKeyDown, { capture: true });
    this.app.canvas.removeEventListener("pointerdown", this.onPointerDown);
    this.inventoryBarElement?.removeEventListener("click", this.onInventoryClick);
    this.shopGridElement?.removeEventListener("click", this.onShopClick);
    this.shopOverlay?.removeEventListener("pointerdown", this.onShopOverlayPointerDown);
    this.shopCloseButton?.removeEventListener("click", this.onShopCloseClick);
    this.easterEggOverlay?.removeEventListener("pointerdown", this.onEasterEggOverlayPointerDown);
    this.easterEggCloseButton?.removeEventListener("click", this.onEasterEggCloseClick);
    this.easterEggLink?.removeEventListener("click", this.onEasterEggLinkClick);
    this.app.ticker.remove(this.tick);
    window.removeEventListener("resize", this.onResize);
    this.worldLayer.destroy({ children: true });
    this.entityLayer.destroy({ children: true });
    this.overlayLayer.destroy({ children: true });
    this.closeShop(false);
    this.closeEasterEgg(false);
    this.options.audio?.stopMerchantVoices();
    this.options.audio?.stopMiningLoop();
    this.options.audio?.stopCampFireBurning();
    this.options.audio?.stopFuelCharge();
    this.options.audio?.stopLowFuelWarning();
    if (this.compassWidget) {
      this.compassWidget.hidden = true;
    }
    if (this.sonarOverlay) {
      this.sonarOverlay.hidden = true;
    }
    if (this.refillIndicator) {
      this.refillIndicator.hidden = true;
    }
    if (this.dealerBubble) {
      this.dealerBubble.hidden = true;
    }
    this.lightContext.clearRect(0, 0, this.lightCanvas.width, this.lightCanvas.height);
  }

  private readonly tick = (ticker: Ticker): void => {
    const deltaSeconds = ticker.deltaMS / 1000;
    this.fuelWavePhase = (this.fuelWavePhase + deltaSeconds * FUEL_WAVE_SPEED) % (Math.PI * 2);
    if (!this.paused && !this.shopOpen && !this.won && !this.gameOver) {
      this.updateFuel(deltaSeconds);
      this.updatePlayer(deltaSeconds);
      this.updateAreaMusic();
      this.checkGameplayInteractions();
      this.runElapsedMs += ticker.deltaMS;
    } else {
      this.updatePlayerAnimation(deltaSeconds, false);
    }
    this.updateCamera();
    this.drawTiles();
    this.drawEntities();
    this.updateMining(deltaSeconds);
    this.drawPlayerAndOverlay();
    this.updateFuelStations(deltaSeconds);
    this.updateActionHint();
    this.updateDealerBubble();
    this.updateSonarOverlay();
    this.updateCompassWidget();
    this.updatePowerupDisplay();
    this.updateLowFuelWarningAudio();
    this.updateFuelWaveClip(clamp(this.fuel, 0, 1));
    this.updateTimerDisplay();
    this.updateFps(deltaSeconds);
  };

  private updatePlayer(deltaSeconds: number): void {
    const axis = this.input.axis();
    const speed = this.level.difficulty.playerSpeed * this.level.tileSize;
    const dx = axis.x * speed * deltaSeconds;
    const dy = axis.y * speed * deltaSeconds;
    const movedX = this.moveAxis(dx, 0);
    const movedY = this.moveAxis(0, dy);
    this.options.audio?.updateFootsteps(movedX || movedY, this.playerFootstepSurface());
    if (axis.x > 0) {
      this.facing = "right";
    } else if (axis.x < 0) {
      this.facing = "left";
    } else if (axis.y > 0) {
      this.facing = "down";
    } else if (axis.y < 0) {
      this.facing = "up";
    }
    this.updatePlayerAnimation(deltaSeconds, movedX || movedY);
    this.resetLockedDoorTouchIfMovedAway();
  }

  private playerFootstepSurface(): FootstepSurface {
    const tileX = Math.floor(this.playerPos.x / this.level.tileSize);
    const tileY = Math.floor(this.playerPos.y / this.level.tileSize);
    const tile = tileAt(this.level, tileX, tileY);
    if (tile.startsWith("K_") || tile.startsWith("KW") || tile.endsWith("2")) return "stone";
    if (tile.endsWith("3")) return "nether";
    return "grass";
  }

  private moveAxis(dx: number, dy: number): boolean {
    if (dx === 0 && dy === 0) return false;
    if (this.noClip) {
      this.playerPos.x += dx;
      this.playerPos.y += dy;
      return true;
    }
    const next = {
      x: this.playerPos.x + dx,
      y: this.playerPos.y + dy,
    };
    if (!this.collides(next.x, next.y)) {
      this.playerPos.x = next.x;
      this.playerPos.y = next.y;
      return true;
    }
    return false;
  }

  private collides(worldX: number, worldY: number): boolean {
    const checks = [
      { x: worldX - PLAYER_HITBOX_HALF_WIDTH, y: worldY - PLAYER_HITBOX_TOP },
      { x: worldX + PLAYER_HITBOX_HALF_WIDTH, y: worldY - PLAYER_HITBOX_TOP },
      { x: worldX - PLAYER_HITBOX_HALF_WIDTH, y: worldY + PLAYER_HITBOX_BOTTOM },
      { x: worldX + PLAYER_HITBOX_HALF_WIDTH, y: worldY + PLAYER_HITBOX_BOTTOM },
    ];
    for (const point of checks) {
      const tileX = Math.floor(point.x / this.level.tileSize);
      const tileY = Math.floor(point.y / this.level.tileSize);
      const tile = tileAt(this.level, tileX, tileY);
      if (isSolidTile(tile) && !this.isPassableDoor(tileX, tileY, tile, point.x, point.y)) {
        return true;
      }
    }
    return this.collidesWithDealer(worldX, worldY);
  }

  private collidesWithDealer(worldX: number, worldY: number): boolean {
    const player = { x: worldX, y: worldY };
    for (const dealer of this.level.entities.dealers) {
      const dealerCenter = this.entityCenter(dealer);
      if (distance(player, dealerCenter) < PLAYER_ENTITY_RADIUS + 25) return true;
      const wagonCenter = { x: dealerCenter.x, y: dealerCenter.y - 20 };
      if (distance(player, wagonCenter) < PLAYER_ENTITY_RADIUS + 46) return true;
    }
    return false;
  }

  private isPassableDoor(tileX: number, tileY: number, tile: string, pointX: number, pointY: number): boolean {
    if (!tile.startsWith("T")) return false;
    const id = tileId(tileX, tileY);
    if (!this.openedDoors.has(id)) {
      const requiredKey = requiredKeyForDoor(tile);
      if (requiredKey === null || !this.ownedKeys.has(requiredKey)) {
        this.playLockedDoorFeedback(id);
        return false;
      }
      this.openDoor(tileX, tileY);
    }
    return this.isDoorPassableAtPoint(tileX, tileY);
  }

  private playLockedDoorFeedback(doorId: string): void {
    if (this.lockedDoorTouchId !== doorId) {
      this.lockedDoorTouchId = doorId;
      this.lockedDoorPlayedForTouch = false;
    }
    if (this.lockedDoorPlayedForTouch) return;
    this.options.audio?.playDoorLockedKnock();
    // No queue: one attempt per contact. Player must leave and touch again.
    this.lockedDoorPlayedForTouch = true;
  }

  private resetLockedDoorTouchIfMovedAway(): void {
    if (!this.lockedDoorTouchId) return;
    const [doorXRaw, doorYRaw] = this.lockedDoorTouchId.split(",");
    const doorX = Number(doorXRaw);
    const doorY = Number(doorYRaw);
    if (!Number.isFinite(doorX) || !Number.isFinite(doorY)) {
      this.lockedDoorTouchId = null;
      this.lockedDoorPlayedForTouch = false;
      return;
    }
    const doorCenter = {
      x: (doorX + 0.5) * this.level.tileSize,
      y: (doorY + 0.5) * this.level.tileSize,
    };
    if (distance(this.playerPos, doorCenter) > this.level.tileSize * 1.05) {
      this.lockedDoorTouchId = null;
      this.lockedDoorPlayedForTouch = false;
    }
  }

  private doorOpenProgress(tileX: number, tileY: number): number {
    const openedAt = this.doorOpenedAt.get(tileId(tileX, tileY));
    if (openedAt === undefined) return 0;
    return clamp((performance.now() - openedAt) / DOOR_ANIMATION_MS, 0, 1);
  }

  private isDoorPassableAtPoint(tileX: number, tileY: number): boolean {
    const progress = this.doorOpenProgress(tileX, tileY);
    if (progress >= 1) return true;
    return false;
  }

  private updateCamera(): void {
    const screen = this.screenSize();
    this.camera.zoom = this.isEffectActive("range") ? RANGE_ZOOM : 1;
    if (this.activePowerUps.has("advancedView")) {
      this.camera.zoom *= ADVANCED_VIEW_ZOOM_FACTOR;
    }
    const worldScreenWidth = screen.width / this.camera.zoom;
    const worldScreenHeight = screen.height / this.camera.zoom;
    const worldWidth = this.level.width * this.level.tileSize;
    const worldHeight = this.level.height * this.level.tileSize;
    this.camera.x = clamp(this.playerPos.x - worldScreenWidth / 2, 0, Math.max(0, worldWidth - worldScreenWidth));
    this.camera.y = clamp(this.playerPos.y - worldScreenHeight / 2, 0, Math.max(0, worldHeight - worldScreenHeight));
  }

  private drawTiles(): void {
    const tileSize = this.level.tileSize;
    const screen = this.screenSize();
    const zoomedTileSize = tileSize * this.camera.zoom;
    const cols = Math.ceil(screen.width / zoomedTileSize) + VIEW_PADDING_TILES * 2;
    const rows = Math.ceil(screen.height / zoomedTileSize) + VIEW_PADDING_TILES * 2;
    if (cols !== this.lastPoolCols || rows !== this.lastPoolRows) {
      this.rebuildTilePool(cols, rows);
    }

    const startCol = Math.floor(this.camera.x / tileSize) - VIEW_PADDING_TILES;
    const startRow = Math.floor(this.camera.y / tileSize) - VIEW_PADDING_TILES;
    let spriteIndex = 0;
    for (let row = 0; row < this.lastPoolRows; row += 1) {
      for (let col = 0; col < this.lastPoolCols; col += 1) {
        const mapX = startCol + col;
        const mapY = startRow + row;
        const sprite = this.tileSprites[spriteIndex];
        spriteIndex += 1;
        const tile = tileAt(this.level, mapX, mapY);
        sprite.texture = this.textures[textureKeyForTile(tile, mapX, mapY, this.level)] ?? Texture.EMPTY;
        sprite.x = Math.floor((mapX * tileSize - this.camera.x) * this.camera.zoom);
        sprite.y = Math.floor((mapY * tileSize - this.camera.y) * this.camera.zoom);
        sprite.width = Math.ceil(zoomedTileSize);
        sprite.height = Math.ceil(zoomedTileSize);
        sprite.visible = mapX >= 0 && mapY >= 0 && mapX < this.level.width && mapY < this.level.height;
      }
    }
  }

  private createEntitySprites(): void {
    for (const entity of this.level.entities.keys) {
      this.addEntitySprite(entity, this.textures[keyTextureKeyForTile(entity.tile)], "key", 18, 31);
    }
    for (const entity of this.level.entities.doors) {
      this.addEntitySprite(entity, this.doorTextureFor(entity, 0), "door", 64, 64);
    }
    for (const entity of this.level.entities.dealers) {
      this.addEntitySprite(entity, this.textures.dealerWagon, "dealerWagon", 100, 100, 0, -20);
      this.addEntitySprite(entity, this.textures.dealer, "dealer", 50, 50);
    }
    for (const entity of this.level.entities.items) {
      const size = itemDisplaySize(entity.itemType);
      this.addEntitySprite(
        entity,
        this.textures[itemTextureKeyForType(entity.itemType)],
        "item",
        size.width,
        size.height,
      );
    }
    for (const entity of this.level.entities.lights) {
      this.addEntitySprite(entity, this.textures[lampTextureKeyForTile(entity.tile)], "light", 64, 64);
    }
  }

  private addEntitySprite(
    entity: LevelEntity,
    texture: Texture,
    kind: EntitySprite["kind"],
    width: number,
    height: number,
    offsetX = 0,
    offsetY = 0,
  ): void {
    const sprite = new Sprite(texture);
    sprite.anchor.set(0.5);
    sprite.roundPixels = true;
    sprite.width = width;
    sprite.height = height;
    this.entityLayer.addChild(sprite);
    this.entitySprites.push({ entity, sprite, kind, width, height, offsetX, offsetY });
  }

  private doorTextureFor(entity: LevelEntity, progress: number): Texture {
    const variant = numericSuffix(entity.tile) ?? 1;
    const frames = this.textures.doorFrames[variant] ?? this.textures.doorFrames[1] ?? [];
    if (frames.length === 0) return this.textures.door;
    const easedProgress = easeSmooth(clamp(progress, 0, 1));
    const index = Math.min(frames.length - 1, Math.round(easedProgress * (frames.length - 1)));
    return frames[index] ?? frames[0] ?? this.textures.door;
  }

  private placeFuelStationAtTile(x: number, y: number): boolean {
    const tile = tileAt(this.level, x, y);
    if (isSolidTile(tile)) return false;
    if (this.fuelStations.some((station) => station.x === x && station.y === y)) return false;
    const entity: LevelEntity = { x, y, tile, itemType: "fuelStation" };
    this.fuelStations.push(entity);
    this.addEntitySprite(entity, this.textures.fuelStation, "fuelStation", 35, 35);
    return true;
  }

  private placeFuelStationAtPlayer(): boolean {
    return this.placeFuelStationAtTile(
      Math.floor(this.playerPos.x / this.level.tileSize),
      Math.floor(this.playerPos.y / this.level.tileSize),
    );
  }

  private drawEntities(): void {
    const tileSize = this.level.tileSize;
    const screen = this.screenSize();
    const refillingStation = this.currentRefillStation();
    for (const { entity, sprite, kind, width, height, offsetX, offsetY } of this.entitySprites) {
      if (entity.tile.startsWith("KY") && this.collectedKeyEntities.has(entityId(entity))) {
        sprite.visible = false;
        continue;
      }
      if (kind === "light" && this.hiddenLights.has(tileId(entity.x, entity.y))) {
        sprite.visible = false;
        continue;
      }
      if (entity.tile.startsWith("T") && this.openedDoors.has(tileId(entity.x, entity.y))) {
        sprite.texture = this.doorTextureFor(entity, this.doorOpenProgress(entity.x, entity.y));
      } else if (kind === "door") {
        sprite.texture = this.doorTextureFor(entity, 0);
      }
      if (entity.itemType && entity.itemType !== "fuelStation" && this.collectedItemEntities.has(entityId(entity))) {
        sprite.visible = false;
        continue;
      }
      const magnetPos = kind === "item" && entity.itemType === "coin" ? this.magnetizedCoins.get(entityId(entity)) : null;
      const baseX = magnetPos?.x ?? (entity.x + 0.5) * tileSize;
      const baseY = magnetPos?.y ?? (entity.y + 0.5) * tileSize;
      const x = (baseX - this.camera.x + offsetX) * this.camera.zoom;
      const y = (baseY - this.camera.y + offsetY) * this.camera.zoom;
      sprite.x = Math.round(x);
      sprite.y = Math.round(y);
      sprite.width = width * this.camera.zoom;
      sprite.height = height * this.camera.zoom;
      sprite.rotation = kind === "door" ? doorRotation(entity.tile) : 0;
      if (kind === "dealer") {
        if (this.shouldDealerTrackPlayer(entity)) {
          const center = this.entityCenter(entity);
          sprite.rotation = Math.atan2(this.playerPos.y - center.y, this.playerPos.x - center.x) - Math.PI / 2;
        } else {
          sprite.rotation = 0;
        }
      }
      if (kind === "fuelStation") {
        const isRefilling = refillingStation?.x === entity.x && refillingStation?.y === entity.y;
        const pulse = isRefilling ? 1 + Math.sin(performance.now() / 120) * 0.08 : 1;
        sprite.scale.set((width / sprite.texture.width) * this.camera.zoom * pulse, (height / sprite.texture.height) * this.camera.zoom * pulse);
      }
      const pad = tileSize * this.camera.zoom;
      sprite.visible = x > -pad && y > -pad && x < screen.width + pad && y < screen.height + pad;
    }
  }

  private checkGameplayInteractions(): void {
    let changed = false;
    changed = this.collectNearbyKeys() || changed;
    changed = this.collectNearbyItems() || changed;
    changed = this.openCurrentDoorIfPossible() || changed;
    changed = this.checkFinish() || changed;
    if (changed) {
      this.updateHudState();
    }
  }

  private collectNearbyItems(): boolean {
    let changed = false;
    for (const entity of this.level.entities.items) {
      const id = entityId(entity);
      if (this.collectedItemEntities.has(id)) continue;
      const centerX = (entity.x + 0.5) * this.level.tileSize;
      const centerY = (entity.y + 0.5) * this.level.tileSize;
      const itemType = entity.itemType ?? "fuel";
      const center = this.magnetizedCoins.get(id) ?? { x: centerX, y: centerY };
      const pickupDistance =
        itemType === "coin" && this.activePowerUps.has("gemMagnet")
          ? this.level.tileSize * COIN_MAGNET_DISTANCE_TILES
          : this.level.tileSize * PICKUP_DISTANCE_TILES;
      const currentDistance = distance(this.playerPos, center);
      if (
        itemType === "coin" &&
        this.activePowerUps.has("gemMagnet") &&
        currentDistance <= pickupDistance &&
        currentDistance > this.level.tileSize * PICKUP_DISTANCE_TILES
      ) {
        const pull = clamp(
          COIN_MAGNET_PULL_MIN + (1 - currentDistance / pickupDistance) * COIN_MAGNET_PULL_BIAS,
          COIN_MAGNET_PULL_MIN,
          COIN_MAGNET_PULL_MAX,
        );
        this.magnetizedCoins.set(id, {
          x: center.x + (this.playerPos.x - center.x) * pull,
          y: center.y + (this.playerPos.y - center.y) * pull,
        });
        continue;
      }
      if (currentDistance > this.level.tileSize * PICKUP_DISTANCE_TILES) continue;
      if (itemType === "fuel") {
        this.fuel = Math.min(1, this.fuel + FUEL_PICKUP_AMOUNT);
        this.options.audio?.playSfx("flame", 0.45);
      } else if (itemType === "coin") {
        this.coins += Math.max(1, Number(entity.value ?? 1));
        this.options.audio?.playSfx("coin", 0.58);
      } else {
        this.addInventoryItem(itemType, Math.max(1, Number(entity.value ?? 1)));
        this.options.audio?.playSfx(itemType === "sonar" ? "sonarPickup" : "item", 0.58);
      }
      this.magnetizedCoins.delete(id);
      this.collectedItemEntities.add(id);
      changed = true;
    }
    return changed;
  }

  private collectNearbyKeys(): boolean {
    let changed = false;
    const pickupDistance = this.level.tileSize * PICKUP_DISTANCE_TILES;
    for (const entity of this.level.entities.keys) {
      const id = entityId(entity);
      if (this.collectedKeyEntities.has(id)) continue;
      const centerX = (entity.x + 0.5) * this.level.tileSize;
      const centerY = (entity.y + 0.5) * this.level.tileSize;
      if (distance(this.playerPos, { x: centerX, y: centerY }) > pickupDistance) continue;
      const keyNumber = numericSuffix(entity.tile);
      if (keyNumber !== null) {
        this.ownedKeys.add(keyNumber);
      }
      this.options.audio?.playSfx("key", 0.68);
      this.collectedKeyEntities.add(id);
      changed = true;
    }
    return changed;
  }

  private openCurrentDoorIfPossible(): boolean {
    const tileX = Math.floor(this.playerPos.x / this.level.tileSize);
    const tileY = Math.floor(this.playerPos.y / this.level.tileSize);
    const tile = tileAt(this.level, tileX, tileY);
    if (!tile.startsWith("T")) return false;
    const id = tileId(tileX, tileY);
    if (this.openedDoors.has(id)) return false;
    const requiredKey = requiredKeyForDoor(tile);
    if (requiredKey === null || !this.ownedKeys.has(requiredKey)) return false;
    return this.openDoor(tileX, tileY);
  }

  private openDoor(tileX: number, tileY: number): boolean {
    const id = tileId(tileX, tileY);
    if (this.openedDoors.has(id)) return false;
    this.openedDoors.add(id);
    this.doorOpenedAt.set(id, performance.now());
    this.options.audio?.playDoorUnlock();
    this.options.audio?.playSfx("doorOpen", 0.62);
    this.updateHudState();
    return true;
  }

  private checkFinish(): boolean {
    const finish = this.level.entities.finish;
    if (!finish || this.won) return false;
    const centerX = (finish.x + 0.5) * this.level.tileSize;
    const centerY = (finish.y + 0.5) * this.level.tileSize;
    if (distance(this.playerPos, { x: centerX, y: centerY }) > this.level.tileSize * 0.52) {
      return false;
    }
    this.won = true;
    this.options.audio?.playSfx("victory", 0.72);
    this.options.audio?.playSfx("impressive", 0.66);
    this.notifyRunEnded("victory");
    return true;
  }

  private drawPlayerAndOverlay(): void {
    const screen = this.screenSize();
    this.player.x = Math.round((this.playerPos.x - this.camera.x) * this.camera.zoom);
    this.player.y = Math.round((this.playerPos.y - this.camera.y) * this.camera.zoom);
    this.player.scale.set(1.35 * this.camera.zoom);

    this.drawLightMask(screen);
  }

  private drawLightMask(screen: { width: number; height: number }): void {
    const dpr = Math.max(1, Math.min(window.devicePixelRatio || 1, 2));
    const canvasWidth = Math.max(1, Math.round(screen.width * dpr));
    const canvasHeight = Math.max(1, Math.round(screen.height * dpr));
    if (this.lightCanvas.width !== canvasWidth || this.lightCanvas.height !== canvasHeight) {
      this.lightCanvas.width = canvasWidth;
      this.lightCanvas.height = canvasHeight;
      this.lightCanvas.style.width = `${screen.width}px`;
      this.lightCanvas.style.height = `${screen.height}px`;
    }

    const ctx = this.lightContext;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, screen.width, screen.height);
    ctx.globalCompositeOperation = "source-over";
    // Keep darkness fully opaque so absolutely unlit areas are true black (OLED-safe).
    ctx.fillStyle = "rgb(0, 0, 0)";
    ctx.fillRect(0, 0, screen.width, screen.height);

    if (!this.gameOver) {
      const fuelRadiusFactor = clamp(Math.pow(this.fuel, PLAYER_LIGHT_FUEL_CURVE_EXPONENT), 0, 1);
      const maxRadius = this.activePowerUps.has("advancedView") ? ADVANCED_PLAYER_LIGHT_RADIUS : BASE_PLAYER_LIGHT_RADIUS;
      const playerWorldRadius = Math.max(0, maxRadius * fuelRadiusFactor);
      const coreRatio = clamp(
        PLAYER_LIGHT_CORE_RATIO_BASE + fuelRadiusFactor * PLAYER_LIGHT_CORE_RATIO_SCALE,
        PLAYER_LIGHT_CORE_RATIO_MIN,
        PLAYER_LIGHT_CORE_RATIO_MAX,
      );
      if (playerWorldRadius > 0) {
        if (PLAYER_LIGHT_OCCLUSION_ENABLED) {
          const playerTileX = Math.floor(this.playerPos.x / this.level.tileSize);
          const playerTileY = Math.floor(this.playerPos.y / this.level.tileSize);
          this.cutSoftLightOccluded(
            this.playerPos,
            playerTileX,
            playerTileY,
            playerWorldRadius,
            coreRatio,
            "player",
          );
        } else {
          this.cutSoftLight(this.player.x, this.player.y, playerWorldRadius * this.camera.zoom, coreRatio);
        }
      }
    }

    for (const light of this.level.entities.lights) {
      if (this.hiddenLights.has(tileId(light.x, light.y))) continue;
      const x = ((light.x + 0.5) * this.level.tileSize - this.camera.x) * this.camera.zoom;
      const y = ((light.y + 0.5) * this.level.tileSize - this.camera.y) * this.camera.zoom;
      const worldRadius = light.lightRadius ?? 170;
      const radius = worldRadius * this.camera.zoom;
      if (x < -radius || y < -radius || x > screen.width + radius || y > screen.height + radius) {
        continue;
      }
      this.cutExternalLight(this.entityCenter(light), light.x, light.y, worldRadius, LAMP_LIGHT_CORE_RATIO, "lamp");
    }
    for (const station of this.fuelStations) {
      const x = ((station.x + 0.5) * this.level.tileSize - this.camera.x) * this.camera.zoom;
      const y = ((station.y + 0.5) * this.level.tileSize - this.camera.y) * this.camera.zoom;
      const worldRadius = FUEL_STATION_LIGHT_RADIUS;
      const radius = worldRadius * this.camera.zoom;
      if (x < -radius || y < -radius || x > screen.width + radius || y > screen.height + radius) {
        continue;
      }
      this.cutExternalLight(
        this.entityCenter(station),
        station.x,
        station.y,
        worldRadius,
        FUEL_STATION_LIGHT_CORE_RATIO,
        "external",
      );
    }
    for (const dealer of this.level.entities.dealers) {
      const center = this.entityCenter(dealer);
      const x = (center.x - this.camera.x) * this.camera.zoom;
      const y = (center.y - this.camera.y) * this.camera.zoom;
      const worldRadius = DEALER_LIGHT_RADIUS;
      const radius = worldRadius * this.camera.zoom;
      if (x < -radius || y < -radius || x > screen.width + radius || y > screen.height + radius) continue;
      this.cutExternalLight(center, dealer.x, dealer.y, worldRadius, DEALER_LIGHT_CORE_RATIO, "external");
    }
    for (const key of this.level.entities.keys) {
      if (this.collectedKeyEntities.has(entityId(key))) continue;
      const center = this.entityCenter(key);
      const x = (center.x - this.camera.x) * this.camera.zoom;
      const y = (center.y - this.camera.y) * this.camera.zoom;
      const worldRadius = KEY_LIGHT_RADIUS;
      const radius = worldRadius * this.camera.zoom;
      if (x < -radius || y < -radius || x > screen.width + radius || y > screen.height + radius) continue;
      this.cutExternalLight(center, key.x, key.y, worldRadius, KEY_LIGHT_CORE_RATIO, "external");
    }
    ctx.globalCompositeOperation = "source-over";
  }

  private cutExternalLight(
    center: Vec2,
    sourceTileX: number,
    sourceTileY: number,
    worldRadius: number,
    coreRatio: number,
    kind: LightOcclusionKind,
  ): void {
    if (NON_PLAYER_LIGHT_OCCLUSION_ENABLED) {
      this.cutSoftLightOccluded(center, sourceTileX, sourceTileY, worldRadius, coreRatio, kind);
      return;
    }
    const screen = this.worldToScreen(center);
    this.cutSoftLight(screen.x, screen.y, worldRadius * this.camera.zoom, coreRatio);
  }

  private cutSoftLightOccluded(
    center: Vec2,
    sourceTileX: number,
    sourceTileY: number,
    worldRadius: number,
    coreRatio: number,
    kind: LightOcclusionKind,
  ): void {
    if (kind === "lamp") {
      this.revealSourceLampTile(sourceTileX, sourceTileY);
    }
    const emitters = this.externalLightEmitters(center, sourceTileX, sourceTileY);
    for (const emitter of emitters) {
      const region = this.collectRayTracedLightRegion(emitter, sourceTileX, sourceTileY, worldRadius);
      if (region.floorTiles.length === 0 && region.wallTiles.length === 0) continue;

      const tileSizeScreen = this.level.tileSize * this.camera.zoom;
      const screenOrigin = this.worldToScreen(emitter.position);
      const radius = worldRadius * this.camera.zoom;
      const ctx = this.lightContext;
      ctx.save();
      ctx.beginPath();
      for (const tile of region.floorTiles) {
        const left = (tile.x * this.level.tileSize - this.camera.x) * this.camera.zoom;
        const top = (tile.y * this.level.tileSize - this.camera.y) * this.camera.zoom;
        ctx.rect(left, top, tileSizeScreen, tileSizeScreen);
      }
      ctx.clip();
      ctx.globalCompositeOperation = "destination-out";
      ctx.fillStyle = this.lightGradient(screenOrigin.x, screenOrigin.y, radius, coreRatio);
      ctx.beginPath();
      ctx.arc(screenOrigin.x, screenOrigin.y, radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      this.drawOccludedWallBleed(region.wallTiles, screenOrigin, radius, coreRatio);
      if (kind === "lamp") {
        this.drawLampImmediateWallBleed(sourceTileX, sourceTileY, screenOrigin, radius, coreRatio);
      }
    }
  }

  private revealSourceLampTile(tileX: number, tileY: number): void {
    const left = (tileX * this.level.tileSize - this.camera.x) * this.camera.zoom;
    const top = (tileY * this.level.tileSize - this.camera.y) * this.camera.zoom;
    const size = this.level.tileSize * this.camera.zoom;
    this.lightContext.globalCompositeOperation = "destination-out";
    this.lightContext.fillStyle = "rgba(0, 0, 0, 1)";
    this.lightContext.fillRect(left, top, size, size);
  }

  private externalLightEmitters(center: Vec2, sourceTileX: number, sourceTileY: number): ExternalLightEmitter[] {
    if (!this.blocksExternalLightAtTile(sourceTileX, sourceTileY)) {
      return [{ position: center, normal: null }];
    }
    const emitters: ExternalLightEmitter[] = [];
    const tileSize = this.level.tileSize;
    for (const step of LIGHT_FLOOD_STEPS) {
      const nextX = sourceTileX + step.dx;
      const nextY = sourceTileY + step.dy;
      if (this.blocksExternalLightAtTile(nextX, nextY)) continue;
      emitters.push({
        position: {
          x: (sourceTileX + 0.5 + step.dx * 0.5) * tileSize + step.dx * tileSize * 0.02,
          y: (sourceTileY + 0.5 + step.dy * 0.5) * tileSize + step.dy * tileSize * 0.02,
        },
        // Emit from the exposed wall surface into the adjacent corridor hemisphere only.
        normal: { x: step.dx, y: step.dy },
      });
    }
    return emitters.length > 0 ? emitters : [{ position: center, normal: null }];
  }

  private collectRayTracedLightRegion(
    emitter: ExternalLightEmitter,
    sourceTileX: number,
    sourceTileY: number,
    worldRadius: number,
  ): { floorTiles: Array<{ x: number; y: number }>; wallTiles: Array<{ x: number; y: number; mask: number }> } {
    const tileSize = this.level.tileSize;
    const origin = emitter.position;
    const maxFloorDistance = worldRadius + tileSize * 0.5;
    const floorMap = new Map<string, { x: number; y: number }>();
    const minTileX = Math.max(0, Math.floor((origin.x - worldRadius - tileSize) / tileSize));
    const maxTileX = Math.min(this.level.width - 1, Math.ceil((origin.x + worldRadius + tileSize) / tileSize));
    const minTileY = Math.max(0, Math.floor((origin.y - worldRadius - tileSize) / tileSize));
    const maxTileY = Math.min(this.level.height - 1, Math.ceil((origin.y + worldRadius + tileSize) / tileSize));

    for (let tileY = minTileY; tileY <= maxTileY; tileY += 1) {
      for (let tileX = minTileX; tileX <= maxTileX; tileX += 1) {
        if (this.blocksExternalLightAtTile(tileX, tileY)) continue;
        const center = this.tileCenter(tileX, tileY);
        const toTarget = { x: center.x - origin.x, y: center.y - origin.y };
        if (emitter.normal && toTarget.x * emitter.normal.x + toTarget.y * emitter.normal.y <= 0) {
          continue;
        }
        if (Math.abs(tileX - sourceTileX) === 1 && Math.abs(tileY - sourceTileY) === 1) {
          // Local corner rule: diagonal path next to source is only lit if at least one orthogonal bridge is open.
          if (
            this.blocksExternalLightAtTile(tileX, sourceTileY) &&
            this.blocksExternalLightAtTile(sourceTileX, tileY)
          ) {
            continue;
          }
        }
        if (distance(origin, center) > maxFloorDistance) continue;
        if (!this.hasExternalLightLineOfSight(origin, center, sourceTileX, sourceTileY)) continue;
        floorMap.set(tileId(tileX, tileY), { x: tileX, y: tileY });
      }
    }

    const wallSides = new Map<string, number>();
    for (const floor of floorMap.values()) {
      for (const step of LIGHT_FLOOD_STEPS) {
        const wallX = floor.x + step.dx;
        const wallY = floor.y + step.dy;
        if (!this.blocksExternalLightAtTile(wallX, wallY)) continue;
        const id = tileId(wallX, wallY);
        wallSides.set(id, (wallSides.get(id) ?? 0) | step.wallSideMask);
      }
    }

    const wallTiles: Array<{ x: number; y: number; mask: number }> = [];
    for (const [id, mask] of wallSides) {
      const [xRaw, yRaw] = id.split(",");
      const x = Number(xRaw);
      const y = Number(yRaw);
      if (!Number.isFinite(x) || !Number.isFinite(y) || mask === 0) continue;
      wallTiles.push({ x, y, mask });
    }
    return { floorTiles: [...floorMap.values()], wallTiles };
  }

  private hasExternalLightLineOfSight(origin: Vec2, target: Vec2, sourceTileX: number, sourceTileY: number): boolean {
    const tileSize = this.level.tileSize;
    let tileX = Math.floor(origin.x / tileSize);
    let tileY = Math.floor(origin.y / tileSize);
    const endTileX = Math.floor(target.x / tileSize);
    const endTileY = Math.floor(target.y / tileSize);
    const deltaX = target.x - origin.x;
    const deltaY = target.y - origin.y;
    const stepX = deltaX > 0 ? 1 : deltaX < 0 ? -1 : 0;
    const stepY = deltaY > 0 ? 1 : deltaY < 0 ? -1 : 0;
    const invDx = deltaX === 0 ? Number.POSITIVE_INFINITY : Math.abs(1 / deltaX);
    const invDy = deltaY === 0 ? Number.POSITIVE_INFINITY : Math.abs(1 / deltaY);
    const boundaryX = stepX > 0 ? (tileX + 1) * tileSize : tileX * tileSize;
    const boundaryY = stepY > 0 ? (tileY + 1) * tileSize : tileY * tileSize;
    let tMaxX = stepX === 0 ? Number.POSITIVE_INFINITY : Math.abs((boundaryX - origin.x) * invDx);
    let tMaxY = stepY === 0 ? Number.POSITIVE_INFINITY : Math.abs((boundaryY - origin.y) * invDy);
    const tDeltaX = stepX === 0 ? Number.POSITIVE_INFINITY : tileSize * invDx;
    const tDeltaY = stepY === 0 ? Number.POSITIVE_INFINITY : tileSize * invDy;
    const epsilon = 1e-7;

    while (tileX !== endTileX || tileY !== endTileY) {
      if (Math.abs(tMaxX - tMaxY) <= epsilon) {
        const nextX = tileX + stepX;
        const nextY = tileY + stepY;
        const sideAX = nextX;
        const sideAY = tileY;
        const sideBX = tileX;
        const sideBY = nextY;
        if (
          !(sideAX === sourceTileX && sideAY === sourceTileY) &&
          this.blocksExternalLightAtTile(sideAX, sideAY)
        ) {
          return false;
        }
        if (
          !(sideBX === sourceTileX && sideBY === sourceTileY) &&
          this.blocksExternalLightAtTile(sideBX, sideBY)
        ) {
          return false;
        }
        tileX = nextX;
        tileY = nextY;
        tMaxX += tDeltaX;
        tMaxY += tDeltaY;
      } else if (tMaxX < tMaxY) {
        tileX += stepX;
        tMaxX += tDeltaX;
      } else {
        tileY += stepY;
        tMaxY += tDeltaY;
      }

      if (tileX === sourceTileX && tileY === sourceTileY) {
        continue;
      }
      if (this.blocksExternalLightAtTile(tileX, tileY)) {
        return false;
      }
    }
    return true;
  }

  private drawOccludedWallBleed(
    wallTiles: Array<{ x: number; y: number; mask: number }>,
    sourceScreen: Vec2,
    radius: number,
    coreRatio: number,
  ): void {
    const tileSizeScreen = this.level.tileSize * this.camera.zoom;
    for (const tile of wallTiles) {
      const left = (tile.x * this.level.tileSize - this.camera.x) * this.camera.zoom;
      const top = (tile.y * this.level.tileSize - this.camera.y) * this.camera.zoom;
      if (tile.mask & WALL_BLEED_LEFT) {
        this.drawWallBleedSide("left", left, top, tileSizeScreen, sourceScreen, radius, coreRatio);
      }
      if (tile.mask & WALL_BLEED_RIGHT) {
        this.drawWallBleedSide("right", left, top, tileSizeScreen, sourceScreen, radius, coreRatio);
      }
      if (tile.mask & WALL_BLEED_TOP) {
        this.drawWallBleedSide("top", left, top, tileSizeScreen, sourceScreen, radius, coreRatio);
      }
      if (tile.mask & WALL_BLEED_BOTTOM) {
        this.drawWallBleedSide("bottom", left, top, tileSizeScreen, sourceScreen, radius, coreRatio);
      }
    }
  }

  private drawLampImmediateWallBleed(
    sourceTileX: number,
    sourceTileY: number,
    sourceScreen: Vec2,
    radius: number,
    coreRatio: number,
  ): void {
    const coverage = LAMP_NEIGHBOR_WALL_BLEED_RATIO;
    const tileSizeScreen = this.level.tileSize * this.camera.zoom;
    for (let dy = -1; dy <= 1; dy += 1) {
      for (let dx = -1; dx <= 1; dx += 1) {
        if (dx === 0 && dy === 0) continue;
        const tileX = sourceTileX + dx;
        const tileY = sourceTileY + dy;
        if (!this.blocksExternalLightAtTile(tileX, tileY)) continue;
        const left = (tileX * this.level.tileSize - this.camera.x) * this.camera.zoom;
        const top = (tileY * this.level.tileSize - this.camera.y) * this.camera.zoom;
        if (Math.abs(dx) + Math.abs(dy) === 1) {
          if (dx === 1) {
            this.drawWallBleedSide("left", left, top, tileSizeScreen, sourceScreen, radius, coreRatio, coverage);
          } else if (dx === -1) {
            this.drawWallBleedSide("right", left, top, tileSizeScreen, sourceScreen, radius, coreRatio, coverage);
          } else if (dy === 1) {
            this.drawWallBleedSide("top", left, top, tileSizeScreen, sourceScreen, radius, coreRatio, coverage);
          } else {
            this.drawWallBleedSide("bottom", left, top, tileSizeScreen, sourceScreen, radius, coreRatio, coverage);
          }
          continue;
        }
        const corner: "tl" | "tr" | "bl" | "br" =
          dx === 1 && dy === 1 ? "tl" : dx === 1 && dy === -1 ? "bl" : dx === -1 && dy === 1 ? "tr" : "br";
        this.drawWallBleedCorner(corner, left, top, tileSizeScreen, sourceScreen, radius, coreRatio, coverage);
      }
    }
  }

  private drawWallBleedSide(
    side: "left" | "right" | "top" | "bottom",
    tileLeft: number,
    tileTop: number,
    tileSizeScreen: number,
    sourceScreen: Vec2,
    radius: number,
    coreRatio: number,
    coverageOverride?: number,
  ): void {
    const coverage = clamp(coverageOverride ?? NON_PLAYER_LIGHT_WALL_BLEED_RATIO, 0.05, 1);
    const fadeStart = clamp(0.5 - coverage / 2, 0, 1);
    const fadeEnd = clamp(0.5 + coverage / 2, 0, 1);
    if (fadeEnd <= 0) return;
    const ctx = this.lightContext;
    const samplePoint = wallBleedSamplePoint(side, tileLeft, tileTop, tileSizeScreen);
    const intensity = this.lightIntensityAtDistance(distance(samplePoint, sourceScreen), radius, coreRatio);
    if (intensity <= 0) return;

    const alpha = clamp(intensity, 0, 1);
    const holdRatio = clamp(fadeStart / Math.max(0.0001, fadeEnd), 0, 1);
    let rectX = tileLeft;
    let rectY = tileTop;
    let rectW = tileSizeScreen;
    let rectH = tileSizeScreen;
    let gradient: CanvasGradient;
    if (side === "left") {
      const extent = tileSizeScreen * fadeEnd;
      rectW = extent;
      gradient = ctx.createLinearGradient(tileLeft, tileTop, tileLeft + extent, tileTop);
      gradient.addColorStop(0, `rgba(0, 0, 0, ${alpha})`);
      gradient.addColorStop(holdRatio, `rgba(0, 0, 0, ${alpha})`);
      gradient.addColorStop(1, "rgba(0, 0, 0, 0)");
    } else if (side === "right") {
      const extent = tileSizeScreen * fadeEnd;
      rectX = tileLeft + tileSizeScreen - extent;
      rectW = extent;
      gradient = ctx.createLinearGradient(tileLeft + tileSizeScreen, tileTop, tileLeft + tileSizeScreen - extent, tileTop);
      gradient.addColorStop(0, `rgba(0, 0, 0, ${alpha})`);
      gradient.addColorStop(holdRatio, `rgba(0, 0, 0, ${alpha})`);
      gradient.addColorStop(1, "rgba(0, 0, 0, 0)");
    } else if (side === "top") {
      const extent = tileSizeScreen * fadeEnd;
      rectH = extent;
      gradient = ctx.createLinearGradient(tileLeft, tileTop, tileLeft, tileTop + extent);
      gradient.addColorStop(0, `rgba(0, 0, 0, ${alpha})`);
      gradient.addColorStop(holdRatio, `rgba(0, 0, 0, ${alpha})`);
      gradient.addColorStop(1, "rgba(0, 0, 0, 0)");
    } else {
      const extent = tileSizeScreen * fadeEnd;
      rectY = tileTop + tileSizeScreen - extent;
      rectH = extent;
      gradient = ctx.createLinearGradient(tileLeft, tileTop + tileSizeScreen, tileLeft, tileTop + tileSizeScreen - extent);
      gradient.addColorStop(0, `rgba(0, 0, 0, ${alpha})`);
      gradient.addColorStop(holdRatio, `rgba(0, 0, 0, ${alpha})`);
      gradient.addColorStop(1, "rgba(0, 0, 0, 0)");
    }

    ctx.globalCompositeOperation = "destination-out";
    ctx.fillStyle = gradient;
    ctx.fillRect(rectX, rectY, rectW, rectH);
  }

  private drawWallBleedCorner(
    corner: "tl" | "tr" | "bl" | "br",
    tileLeft: number,
    tileTop: number,
    tileSizeScreen: number,
    sourceScreen: Vec2,
    radius: number,
    coreRatio: number,
    coverage: number,
  ): void {
    const extent = tileSizeScreen * clamp(coverage, 0.05, 1);
    const point = cornerPoint(corner, tileLeft, tileTop, tileSizeScreen);
    const intensity = this.lightIntensityAtDistance(distance(point, sourceScreen), radius, coreRatio);
    if (intensity <= 0) return;
    const alpha = clamp(intensity, 0, 1);
    let rectX = tileLeft;
    let rectY = tileTop;
    if (corner === "tr" || corner === "br") {
      rectX = tileLeft + tileSizeScreen - extent;
    }
    if (corner === "bl" || corner === "br") {
      rectY = tileTop + tileSizeScreen - extent;
    }
    const gradient = this.lightContext.createRadialGradient(point.x, point.y, 0, point.x, point.y, extent);
    gradient.addColorStop(0, `rgba(0, 0, 0, ${alpha})`);
    gradient.addColorStop(1, "rgba(0, 0, 0, 0)");
    this.lightContext.globalCompositeOperation = "destination-out";
    this.lightContext.fillStyle = gradient;
    this.lightContext.fillRect(rectX, rectY, extent, extent);
  }

  private blocksExternalLightAtTile(tileX: number, tileY: number): boolean {
    const tile = tileAt(this.level, tileX, tileY);
    if (isWallTile(tile)) return true;
    if (!tile.startsWith("T")) return false;
    if (!this.openedDoors.has(tileId(tileX, tileY))) return true;
    return this.doorOpenProgress(tileX, tileY) < 1;
  }

  private distanceToTileCenter(center: Vec2, tileX: number, tileY: number): number {
    return distance(center, this.tileCenter(tileX, tileY));
  }

  private tileCenter(tileX: number, tileY: number): Vec2 {
    return {
      x: (tileX + 0.5) * this.level.tileSize,
      y: (tileY + 0.5) * this.level.tileSize,
    };
  }

  private cutSoftLight(x: number, y: number, radius: number, coreRatio: number): void {
    const ctx = this.lightContext;
    ctx.globalCompositeOperation = "destination-out";
    ctx.fillStyle = this.lightGradient(x, y, radius, coreRatio);
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
  }

  private lightGradient(x: number, y: number, radius: number, coreRatio: number): CanvasGradient {
    const normalizedCoreRatio = clamp(coreRatio, 0.05, 0.9);
    const gradient = this.lightContext.createRadialGradient(x, y, 0, x, y, radius);
    gradient.addColorStop(0, "rgba(0, 0, 0, 1)");
    gradient.addColorStop(normalizedCoreRatio, "rgba(0, 0, 0, 1)");
    gradient.addColorStop(0.78, "rgba(0, 0, 0, 0.46)");
    gradient.addColorStop(1, "rgba(0, 0, 0, 0)");
    return gradient;
  }

  private lightIntensityAtDistance(distancePx: number, radius: number, coreRatio: number): number {
    if (!Number.isFinite(radius) || radius <= 0) return 0;
    const normalizedCoreRatio = clamp(coreRatio, 0.05, 0.9);
    const t = clamp(distancePx / radius, 0, 1);
    if (t <= normalizedCoreRatio) return 1;
    if (t <= 0.78) {
      const span = Math.max(0.0001, 0.78 - normalizedCoreRatio);
      return lerp(1, 0.46, (t - normalizedCoreRatio) / span);
    }
    return lerp(0.46, 0, (t - 0.78) / 0.22);
  }

  private updateFuel(deltaSeconds: number): void {
    if (this.fuelFrozen) {
      this.updateHudState();
      return;
    }
    if (this.isPlayerInLitZone()) {
      this.updateHudState();
      return;
    }
    const drainPerSecond = (this.level.difficulty.lightDrain / 0.3) * this.fuelDrainMultiplier;
    this.fuel = Math.max(0, this.fuel - drainPerSecond * deltaSeconds);
    if (this.fuel <= 0) {
      this.gameOver = true;
      this.options.audio?.playRandomDeathVoiceLine(0.68);
      this.notifyRunEnded("gameOver");
    }
    this.updateHudState();
  }

  private updateFuelStations(deltaSeconds: number): void {
    const station = this.currentRefillStation();
    const stationNearbyAndActive = !this.paused && !this.shopOpen && !this.won && !this.gameOver && station !== null;
    if (stationNearbyAndActive) {
      this.options.audio?.startCampFireBurning();
    } else {
      this.options.audio?.stopCampFireBurning();
    }
    const shouldRefill = stationNearbyAndActive && this.fuel < 1;
    if (!shouldRefill) {
      this.refillingSince = 0;
      this.options.audio?.stopFuelCharge();
      if (this.refillIndicator) this.refillIndicator.hidden = true;
      return;
    }
    if (this.refillingSince === 0) {
      this.refillingSince = performance.now();
    }
    if (performance.now() - this.refillingSince >= REFILL_SOUND_DELAY_MS) {
      this.options.audio?.startFuelCharge();
    }
    this.fuel = Math.min(1, this.fuel + REFILL_RATE_PER_SECOND * deltaSeconds);
    if (this.fuel >= 1) {
      this.options.audio?.stopFuelCharge();
    }
    this.updateHudState();
  }

  private currentRefillStation(): LevelEntity | null {
    const refillDistance = this.level.tileSize * REFILL_DISTANCE_TILES;
    for (const station of this.fuelStations) {
      if (distance(this.playerPos, this.entityCenter(station)) <= refillDistance) {
        return station;
      }
    }
    return null;
  }

  private isPlayerInLitZone(): boolean {
    if (this.currentRefillStation()) return true;
    for (const light of this.level.entities.lights) {
      if (this.hiddenLights.has(tileId(light.x, light.y))) continue;
      const radius = (light.lightRadius ?? 170) * LAMP_LIT_ZONE_FACTOR;
      if (distance(this.playerPos, this.entityCenter(light)) <= radius) return true;
    }
    for (const dealer of this.level.entities.dealers) {
      if (distance(this.playerPos, this.entityCenter(dealer)) <= DEALER_LIT_ZONE_RADIUS) return true;
    }
    return false;
  }

  private entityCenter(entity: LevelEntity): Vec2 {
    return {
      x: (entity.x + 0.5) * this.level.tileSize,
      y: (entity.y + 0.5) * this.level.tileSize,
    };
  }

  private worldToScreen(position: Vec2): Vec2 {
    return {
      x: (position.x - this.camera.x) * this.camera.zoom,
      y: (position.y - this.camera.y) * this.camera.zoom,
    };
  }

  private rebuildTilePool(cols?: number, rows?: number): void {
    const screen = this.screenSize();
    const zoomedTileSize = this.level.tileSize * this.camera.zoom;
    this.lastPoolCols = cols ?? Math.ceil(screen.width / zoomedTileSize) + VIEW_PADDING_TILES * 2;
    this.lastPoolRows = rows ?? Math.ceil(screen.height / zoomedTileSize) + VIEW_PADDING_TILES * 2;

    this.worldLayer.removeChildren();
    this.tileSprites.length = 0;
    const total = this.lastPoolCols * this.lastPoolRows;
    for (let index = 0; index < total; index += 1) {
      const sprite = new Sprite(Texture.EMPTY);
      sprite.roundPixels = true;
      this.tileSprites.push(sprite);
      this.worldLayer.addChild(sprite);
    }
  }

  private screenSize(): { width: number; height: number } {
    return {
      width: this.app.screen.width,
      height: this.app.screen.height,
    };
  }

  private updateHudMeta(): void {
    if (this.hudMetaElement) {
      this.hudMetaElement.textContent = `${this.level.difficulty.label} · Seed ${this.level.seed} · ${this.level.width}x${this.level.height}`;
    }
  }

  private updateHudState(): void {
    this.updateKeychainDisplay();
    this.updateInventoryDisplay();
    this.updateGemDisplay();
    this.updateFuelDisplay();
    if (!this.hudStateElement) return;
    const text = "";
    if (text !== this.lastHudStateText) {
      this.hudStateElement.textContent = text;
      this.lastHudStateText = text;
    }
  }

  private updateGemDisplay(): void {
    if (this.gemCountElement) {
      this.gemCountElement.textContent = String(this.coins);
    }
  }

  private updateFuelDisplay(): void {
    const level = clamp(this.fuel, 0, 1);
    this.fuelTankElement?.style.setProperty("--fuel-level", String(level));
    this.updateFuelWaveClip(level);
  }

  private updateFuelWaveClip(level: number): void {
    const fillElement = this.fuelTankFillElement;
    if (!fillElement) return;
    const width = Math.max(1, fillElement.clientWidth);
    const height = Math.max(1, fillElement.clientHeight);

    const normalizedLevel = clamp((level - FUEL_WAVE_VISUAL_DEADZONE) / (1 - FUEL_WAVE_VISUAL_DEADZONE), 0, 1);
    const visibleLevel = Math.pow(normalizedLevel, FUEL_WAVE_VISUAL_CURVE);

    if (visibleLevel <= 0.0001) {
      const emptyClip = "polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)";
      if (emptyClip !== this.lastFuelWaveClip) {
        fillElement.style.setProperty("--fuel-wave-clip", emptyClip);
        this.lastFuelWaveClip = emptyClip;
      }
      return;
    }

    const amplitudePx = Math.max(0.4, height * FUEL_WAVE_AMPLITUDE_RATIO * (0.38 + visibleLevel * 0.62));
    const topY = (1 - visibleLevel) * height;
    const points: string[] = ["0% 100%"];

    for (let index = 0; index <= FUEL_WAVE_SEGMENTS; index += 1) {
      const t = index / FUEL_WAVE_SEGMENTS;
      const x = t * width;
      const y =
        topY +
        Math.sin((x / FUEL_WAVE_LENGTH_PX) * Math.PI * 2 + this.fuelWavePhase) * amplitudePx;
      const xPct = (x / width) * 100;
      const yPct = (clamp(y, 0, height) / height) * 100;
      points.push(`${xPct.toFixed(3)}% ${yPct.toFixed(3)}%`);
    }

    points.push("100% 100%");
    const clip = `polygon(${points.join(", ")})`;
    if (clip === this.lastFuelWaveClip) return;
    fillElement.style.setProperty("--fuel-wave-clip", clip);
    this.lastFuelWaveClip = clip;
  }

  private updateTimerDisplay(): void {
    if (!this.timerElement) return;
    const text = formatElapsed(this.runElapsedMs);
    if (text === this.lastTimerText) return;
    this.timerElement.textContent = text;
    this.lastTimerText = text;
  }

  private updateKeychainDisplay(): void {
    if (!this.keychainImage) return;
    const index = Math.min(this.ownedKeys.size, keychainUrls.length - 1);
    if (index === this.lastKeychainIndex) return;
    this.keychainImage.src = keychainUrls[index];
    this.lastKeychainIndex = index;
  }

  private updateInventoryDisplay(): void {
    const inventoryBar = this.inventoryBarElement;
    if (!inventoryBar) return;
    const active = this.activeEffectTypes().join(",");
    const signature = `${active}|${this.inventorySlots.map((slot) => (slot ? `${slot.type}:${slot.count}` : "-")).join(",")}`;
    if (signature === this.lastInventorySignature) return;

    inventoryBar.replaceChildren();
    this.inventorySlots.forEach((entry, index) => {
      if (!entry) return;
      const { type, count } = entry;
      const position = INVENTORY_SLOT_POSITIONS[index];
      const slot = document.createElement("button");
      slot.type = "button";
      slot.className = "inventory-slot";
      slot.dataset.slotIndex = String(index);
      slot.style.left = `${(position.x / 400) * 100}%`;
      slot.style.top = `${(position.y / 103) * 100}%`;
      slot.classList.toggle("is-active", this.isEffectActive(type));
      const cooldown = this.itemCooldowns.get(type);
      if (cooldown) {
        const elapsed = performance.now() - cooldown.startedAt;
        if (elapsed < cooldown.durationMs) {
          slot.classList.add("is-cooling");
          slot.style.setProperty("--cooldown-duration-ms", `${Math.max(80, cooldown.durationMs - elapsed)}ms`);
        } else {
          this.itemCooldowns.delete(type);
        }
      }

      const icon = document.createElement("img");
      icon.alt = "";
      icon.src = inventoryIconUrl(type);
      slot.appendChild(icon);

      const badge = document.createElement("span");
      badge.textContent = String(count);
      slot.appendChild(badge);

      inventoryBar.appendChild(slot);
    });
    this.lastInventorySignature = signature;
  }

  private updatePowerupDisplay(): void {
    const powerupBar = this.powerupBarElement;
    if (!powerupBar) return;
    const signature = POWERUP_ORDER.filter((type) => this.activePowerUps.has(type)).join(",");
    if (signature === this.lastPowerupSignature) return;
    powerupBar.replaceChildren();
    for (const type of POWERUP_ORDER) {
      if (!this.activePowerUps.has(type)) continue;
      const icon = document.createElement("img");
      icon.className = "powerup-icon";
      icon.alt = "";
      icon.src = inventoryIconUrl(type);
      powerupBar.appendChild(icon);
    }
    this.lastPowerupSignature = signature;
  }

  private inventoryEntries(): Array<[string, number]> {
    return [...this.inventoryCounts.entries()]
      .filter(([, count]) => count > 0)
      .sort(([a], [b]) => {
        const aIndex = INVENTORY_ORDER.indexOf(a as InventoryItemType);
        const bIndex = INVENTORY_ORDER.indexOf(b as InventoryItemType);
        return (aIndex === -1 ? 99 : aIndex) - (bIndex === -1 ? 99 : bIndex);
      });
  }

  private readonly onGameKeyDown = (event: KeyboardEvent): void => {
    if (isEditableTarget(event.target)) return;
    if (event.repeat || this.won || this.gameOver) return;
    if (this.easterEggOpen) {
      if (event.code === "Escape" || event.code === "KeyE" || event.code === "KeyI") {
        this.closeEasterEgg();
        event.preventDefault();
        event.stopPropagation();
      }
      return;
    }
    if (this.shopOpen) {
      if (event.code === "Escape" || event.code === "KeyE" || event.code === "KeyI") {
        this.closeShop();
        event.preventDefault();
        event.stopPropagation();
      }
      return;
    }
    if (event.code === "KeyE" || event.code === "KeyI") {
      const dealer = this.nearbyDealer();
      if (dealer) {
        this.openShop(dealer);
        event.preventDefault();
        event.stopPropagation();
      }
      return;
    }
    if (this.paused) return;
    const slotIndex = inventorySlotIndexFromCode(event.code);
    if (slotIndex === null) return;
    if (this.useInventorySlot(slotIndex)) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  private readonly onInventoryClick = (event: MouseEvent): void => {
    if (this.paused || this.won || this.gameOver) return;
    const target = event.target instanceof Element ? event.target.closest<HTMLButtonElement>("[data-slot-index]") : null;
    if (!target) return;
    const slotIndex = Number(target.dataset.slotIndex);
    if (Number.isInteger(slotIndex)) {
      this.useInventorySlot(slotIndex);
    }
  };

  private readonly onPointerDown = (event: PointerEvent): void => {
    if (this.paused || this.won || this.gameOver || this.shopOpen || this.easterEggOpen) return;
    const dealer = this.nearbyDealer();
    if (!dealer) return;
    const rect = this.app.canvas.getBoundingClientRect();
    const click = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
    const dealerScreen = this.worldToScreen(this.entityCenter(dealer));
    if (distance(click, dealerScreen) > this.level.tileSize * this.camera.zoom * 1.35) return;
    this.openShop(dealer);
  };

  private useInventorySlot(slotIndex: number): boolean {
    const entry = this.inventorySlots[slotIndex];
    if (!entry) return false;
    const { type, count } = entry;
    if (count <= 0) return false;
    if (type === "range") {
      if (this.isEffectActive("range")) return false;
      this.rangeUntil = performance.now() + RANGE_DURATION_MS;
      this.startItemCooldown(type, RANGE_DURATION_MS);
      this.options.audio?.playSfx("rangeUp", 0.58);
    } else if (type === "sonar") {
      if (this.isEffectActive("sonar")) return false;
      this.sonarUntil = performance.now() + SONAR_DURATION_MS;
      this.startItemCooldown(type, SONAR_DURATION_MS);
      this.lastSonarSignature = "";
      this.options.audio?.playSfx("sonar", 0.62);
    } else if (type === "fuelStation") {
      if (!this.placeFuelStationAtPlayer()) return false;
    } else if (type === "compass") {
      if (this.isEffectActive("compass")) return false;
      this.compassUntil = performance.now() + COMPASS_DURATION_MS;
      this.startItemCooldown(type, COMPASS_DURATION_MS);
      this.options.audio?.playSfx("item", 0.5);
    } else if (type === "pickaxe") {
      return this.startMiningFacingWall(slotIndex);
    } else {
      return false;
    }
    this.decrementInventorySlot(slotIndex);
    this.lastInventorySignature = "";
    this.updateHudState();
    return true;
  }

  private activeEffectTypes(): string[] {
    const active = [];
    if (this.isEffectActive("range")) active.push("range");
    if (this.isEffectActive("sonar")) active.push("sonar");
    if (this.isEffectActive("compass")) active.push("compass");
    if (this.isEffectActive("pickaxe")) active.push("pickaxe");
    return active;
  }

  private isEffectActive(type: string): boolean {
    const now = performance.now();
    if (type === "range") return now < this.rangeUntil;
    if (type === "sonar") return now < this.sonarUntil;
    if (type === "compass") return now < this.compassUntil;
    if (type === "pickaxe") return this.miningJob !== null || (now < this.pickaxeArmedUntil && this.inventoryCount("pickaxe") > 0);
    return false;
  }

  private startItemCooldown(type: string, durationMs: number): void {
    this.itemCooldowns.set(type, { startedAt: performance.now(), durationMs });
    this.lastInventorySignature = "";
  }

  private addInventoryItem(type: string, count: number): void {
    const existingSlot = this.inventorySlots.find((slot) => slot?.type === type);
    if (existingSlot) {
      existingSlot.count += count;
    } else {
      const emptyIndex = this.inventorySlots.findIndex((slot) => slot === null);
      if (emptyIndex >= 0) {
        this.inventorySlots[emptyIndex] = { type, count };
      } else {
        return;
      }
    }
    this.inventoryCounts.set(type, (this.inventoryCounts.get(type) ?? 0) + count);
    this.lastInventorySignature = "";
  }

  private decrementInventoryItem(type: string): void {
    const slotIndex = this.inventorySlots.findIndex((slot) => slot?.type === type);
    if (slotIndex >= 0) {
      this.decrementInventorySlot(slotIndex);
    }
  }

  private decrementInventorySlot(slotIndex: number): void {
    const slot = this.inventorySlots[slotIndex];
    if (!slot) return;
    const count = this.inventoryCounts.get(slot.type) ?? 0;
    if (slot.count <= 1) {
      this.inventorySlots[slotIndex] = null;
    } else {
      slot.count -= 1;
    }
    if (count <= 1) {
      this.inventoryCounts.delete(slot.type);
    } else {
      this.inventoryCounts.set(slot.type, count - 1);
    }
    this.lastInventorySignature = "";
  }

  private inventoryCount(type: string): number {
    return this.inventoryCounts.get(type) ?? 0;
  }

  private removeInventoryType(type: string): void {
    const slotIndex = this.inventorySlots.findIndex((slot) => slot?.type === type);
    if (slotIndex >= 0) {
      this.inventorySlots[slotIndex] = null;
      this.inventoryCounts.delete(type);
    }
    this.lastInventorySignature = "";
  }

  private readonly onShopClick = (event: MouseEvent): void => {
    const button = event.target instanceof Element ? event.target.closest<HTMLButtonElement>("[data-shop-item]") : null;
    if (!button || button.disabled) return;
    const type = button.dataset.shopItem as ShopOffer["type"] | undefined;
    if (type) {
      this.buyShopItem(type);
    }
  };

  private readonly onShopOverlayPointerDown = (event: PointerEvent): void => {
    if (!this.shopOpen || !this.shopOverlay) return;
    if (event.target !== this.shopOverlay) return;
    event.preventDefault();
    this.closeShop();
  };

  private readonly onShopCloseClick = (event: MouseEvent): void => {
    if (!this.shopOpen) return;
    event.preventDefault();
    this.closeShop();
  };

  private readonly onEasterEggOverlayPointerDown = (event: PointerEvent): void => {
    if (!this.easterEggOpen || !this.easterEggOverlay) return;
    if (event.target !== this.easterEggOverlay) return;
    event.preventDefault();
    this.closeEasterEgg();
  };

  private readonly onEasterEggCloseClick = (event: MouseEvent): void => {
    if (!this.easterEggOpen) return;
    event.preventDefault();
    this.closeEasterEgg();
  };

  private readonly onEasterEggLinkClick = (): void => {
    if (!this.easterEggOpen) return;
    this.closeEasterEgg(false);
  };

  private nearbyDealer(): LevelEntity | null {
    let closest: LevelEntity | null = null;
    let closestDistance = Number.POSITIVE_INFINITY;
    const range = this.level.tileSize * DEALER_INTERACTION_DISTANCE_TILES;
    for (const dealer of this.level.entities.dealers) {
      const center = {
        x: (dealer.x + 0.5) * this.level.tileSize,
        y: (dealer.y + 0.5) * this.level.tileSize,
      };
      const currentDistance = distance(this.playerPos, center);
      if (currentDistance <= range && currentDistance < closestDistance) {
        closest = dealer;
        closestDistance = currentDistance;
      }
    }
    return closest;
  }

  private openShop(dealer: LevelEntity): void {
    if (this.shopOpen && this.shopDealer?.x === dealer.x && this.shopDealer?.y === dealer.y) return;
    this.shopOpen = true;
    this.shopDealer = dealer;
    if (this.shopOverlay) {
      this.shopOverlay.hidden = false;
    }
    this.options.audio?.playVoiceLine("shopBuying", 0.72);
    this.renderShop("E, Escape oder Tippen außerhalb schließt den Shop.");
    this.updateHudState();
  }

  private closeShop(playVoice = true): void {
    const wasOpen = this.shopOpen;
    this.shopOpen = false;
    this.shopDealer = null;
    if (this.easterEggOpen) {
      this.closeEasterEgg(false);
    }
    if (this.shopOverlay) {
      this.shopOverlay.hidden = true;
    }
    if (playVoice && wasOpen) {
      this.options.audio?.playVoiceLine("shopAnytime", 0.58);
    }
    this.updateHudState();
  }

  private openEasterEgg(): void {
    if (!this.easterEggOverlay) return;
    this.easterEggOpen = true;
    this.easterEggOverlay.hidden = false;
    this.updateHudState();
  }

  private closeEasterEgg(updateHud = true): void {
    this.easterEggOpen = false;
    if (this.easterEggOverlay) {
      this.easterEggOverlay.hidden = true;
    }
    if (updateHud) {
      this.updateHudState();
    }
  }

  private renderShop(status = ""): void {
    if (!this.shopGridElement) return;
    const goods = this.shopGoodsForDealer(this.shopDealer);
    this.lastShopSelectionSoundFor = "";
    this.shopGridElement.replaceChildren();
    if (this.shopGemsElement) {
      this.shopGemsElement.textContent = `${this.coins} Gems`;
    }
    for (const type of goods) {
      const offer = SHOP_CATALOG[type];
      const isPowerUpActive = offer.kind === "powerup" && this.activePowerUps.has(type as PowerUpType);
      const requiresCoins = offer.kind !== "special" || !this.easterEggUnlocked;
      const disabled = (requiresCoins && this.coins < offer.price) || isPowerUpActive;
      const button = document.createElement("button");
      button.type = "button";
      button.className = "shop-item";
      button.dataset.shopItem = offer.type;
      button.disabled = disabled;
      button.classList.toggle("is-disabled", disabled);

      const icon = document.createElement("img");
      icon.alt = "";
      icon.src = inventoryIconUrl(offer.type);
      button.appendChild(icon);

      const label = document.createElement("strong");
      label.textContent = offer.label;
      button.appendChild(label);

      const description = document.createElement("span");
      description.textContent = offer.description;
      button.appendChild(description);

      const price = document.createElement("span");
      price.textContent =
        offer.kind === "powerup" && this.activePowerUps.has(type as PowerUpType)
          ? "Bereits aktiv"
          : offer.kind === "special" && this.easterEggUnlocked
            ? "Freigeschaltet"
          : `${offer.price} Gems`;
      button.appendChild(price);

      button.addEventListener("pointerenter", () => this.playShopSelectionLow(offer.type));
      button.addEventListener("focus", () => this.playShopSelectionLow(offer.type));

      this.shopGridElement.appendChild(button);
    }
    if (this.shopStatusElement) {
      this.shopStatusElement.textContent = status;
    }
  }

  private shopGoodsForDealer(dealer: LevelEntity | null): ShopItemType[] {
    if (!dealer) return SHOP_ASSORTMENTS[0];
    const index = Math.max(0, this.level.entities.dealers.findIndex((entity) => entity.x === dealer.x && entity.y === dealer.y));
    return SHOP_ASSORTMENTS[index % SHOP_ASSORTMENTS.length];
  }

  private buyShopItem(type: ShopOffer["type"]): void {
    const offer = SHOP_CATALOG[type];
    const needsPayment = offer.kind !== "special" || !this.easterEggUnlocked;
    if (needsPayment && this.coins < offer.price) {
      this.options.audio?.playSfx("noMoney", 0.6);
      this.options.audio?.playVoiceLine("shopNoMoney", 0.68);
      this.renderShop("Nicht genug Gems.");
      return;
    }
    if (offer.kind === "special") {
      if (!this.easterEggUnlocked) {
        this.coins -= offer.price;
        this.easterEggUnlocked = true;
        this.options.audio?.playSfx("buyItem", 0.62);
        this.options.audio?.playSfx("shopEnoughMoney", 0.56);
        this.options.audio?.playVoiceLine("shopThankYou", 0.58);
      }
      this.openEasterEgg();
      this.renderShop("Geheimnis entdeckt.");
      this.updateHudState();
      return;
    }
    if (offer.kind === "powerup") {
      if (this.activePowerUps.has(type as PowerUpType)) {
        this.renderShop("Powerup ist bereits aktiv.");
        return;
      }
      this.activatePowerUp(type as PowerUpType);
    } else {
      this.addInventoryItem(type, 1);
    }
    this.coins -= offer.price;
    this.options.audio?.playSfx("buyItem", 0.62);
    this.options.audio?.playSfx("shopEnoughMoney", 0.56);
    this.options.audio?.playVoiceLine("shopThankYou", 0.58);
    this.renderShop(`${offer.label} gekauft.`);
    this.updateHudState();
  }

  private playShopSelectionLow(type: ShopOffer["type"]): void {
    if (this.lastShopSelectionSoundFor === type) return;
    this.lastShopSelectionSoundFor = type;
    this.options.audio?.playSfx("shopSelectionLow", 0.38);
  }

  private activatePowerUp(type: PowerUpType): void {
    this.activePowerUps.add(type);
    if (type === "betterTank") {
      this.fuelDrainMultiplier = Math.min(this.fuelDrainMultiplier, 2 / 3);
    }
    this.lastPowerupSignature = "";
  }

  private updateActionHint(): void {
    if (!this.actionHintElement) return;
    let text = "";
    if (this.shopOpen) {
      text = "";
    } else if (this.isEffectActive("pickaxe")) {
      text = "Pickaxe arbeitet";
    } else if (this.nearbyDealer()) {
      text = "E / I Händler";
    }
    if (text !== this.lastActionHintText) {
      this.actionHintElement.textContent = text;
      this.lastActionHintText = text;
    }
  }

  private updateAreaMusic(): void {
    const tileX = Math.floor(this.playerPos.x / this.level.tileSize);
    const tileY = Math.floor(this.playerPos.y / this.level.tileSize);
    const tile = tileAt(this.level, tileX, tileY);
    const region = resolveAmbientRegion(this.level, tileX, tileY, this.activeMusicRegion);
    if (region !== null) {
      this.activeMusicRegion = region;
    }
    const resolvedRegion = region ?? this.activeMusicRegion ?? 1;
    const nextMusic = isDealerHouseTile(tile) ? "saveSpace" : musicForRegion(resolvedRegion);
    if (nextMusic === this.activeMusicKey) return;
    this.activeMusicKey = nextMusic;
    this.options.audio?.playMusic(nextMusic);
  }

  private shouldDealerTrackPlayer(dealer: LevelEntity): boolean {
    const tileX = Math.floor(this.playerPos.x / this.level.tileSize);
    const tileY = Math.floor(this.playerPos.y / this.level.tileSize);
    const playerTile = tileAt(this.level, tileX, tileY);
    const playerRegion = numericSuffix(playerTile);
    const dealerRegion = numericSuffix(dealer.tile);
    const inSameHouse = playerTile.startsWith("K_") && playerRegion !== null && playerRegion === dealerRegion;
    if (!inSameHouse) return false;
    const maxDistance = this.level.tileSize * DEALER_LOOK_DISTANCE_TILES;
    return distance(this.playerPos, this.entityCenter(dealer)) <= maxDistance;
  }

  private updateDealerBubble(): void {
    if (!this.dealerBubble || this.shopOpen || this.won || this.gameOver) {
      if (this.dealerBubble) this.dealerBubble.hidden = true;
      return;
    }
    const dealer = this.nearbyDealer();
    if (!dealer) {
      this.dealerBubble.hidden = true;
      return;
    }
    const tips = this.dealerTipsFor(dealer);
    const now = performance.now();
    if (now - this.dealerTipAt > DEALER_BUBBLE_CYCLE_MS || this.dealerTipIndex < 0) {
      this.dealerTipAt = now;
      this.dealerTipIndex = (this.dealerTipIndex + 1) % tips.length;
    }
    const screenPos = this.worldToScreen({
      x: (dealer.x + 0.5) * this.level.tileSize,
      y: (dealer.y - 0.35) * this.level.tileSize,
    });
    const bubbleOffsetX = this.level.tileSize * this.camera.zoom * 0.25;
    const bubbleOffsetY = this.level.tileSize * this.camera.zoom * 0.5;
    this.dealerBubble.textContent = tips[this.dealerTipIndex] ?? tips[0];
    this.dealerBubble.style.left = `${Math.round(screenPos.x + 8 + bubbleOffsetX)}px`;
    this.dealerBubble.style.top = `${Math.round(screenPos.y + bubbleOffsetY)}px`;
    this.dealerBubble.hidden = false;
  }

  private dealerTipsFor(dealer: LevelEntity): string[] {
    const goods = this.shopGoodsForDealer(dealer).map((type) => SHOP_CATALOG[type].label);
    return [
      "Was darf es sein?",
      `Heute im Angebot: ${goods.slice(0, 3).join(", ")}.`,
      "Camp Fires laden dich auf und halten die Dunkelheit fern.",
      "Keys leuchten. Manchmal lohnt sich ein weiter Blick.",
    ];
  }

  private updateCompassWidget(): void {
    if (!this.compassWidget || !this.compassNeedle) return;
    if (!this.isEffectActive("compass")) {
      this.compassWidget.hidden = true;
      return;
    }
    const target = this.compassTarget();
    if (!target) {
      this.compassWidget.hidden = true;
      return;
    }
    const dx = target.x - this.playerPos.x;
    const dy = target.y - this.playerPos.y;
    const angle = Math.atan2(dy, dx) + Math.PI / 2;
    this.compassWidget.hidden = false;
    this.compassNeedle.style.transform = `rotate(${angle}rad)`;
  }

  private updateSonarOverlay(): void {
    if (!this.sonarOverlay || !this.sonarCanvas || !this.sonarContext) return;
    if (!this.isEffectActive("sonar")) {
      this.sonarOverlay.hidden = true;
      return;
    }
    const screen = this.screenSize();
    const cssWidth = screen.width;
    const cssHeight = screen.height;
    const dpr = Math.max(1, Math.min(window.devicePixelRatio || 1, 2));
    const cellSize = Math.max(1, this.level.tileSize * this.camera.zoom);
    const signature = [
      cssWidth,
      cssHeight,
      dpr,
      this.camera.x.toFixed(2),
      this.camera.y.toFixed(2),
      this.camera.zoom.toFixed(3),
      this.playerPos.x.toFixed(2),
      this.playerPos.y.toFixed(2),
      this.minedWalls.size,
      this.collectedKeyEntities.size,
      this.openedDoors.size,
    ].join(":");
    if (signature === this.lastSonarSignature && !this.sonarOverlay.hidden) return;

    this.sonarOverlay.hidden = false;
    this.sonarCanvas.style.width = `${cssWidth}px`;
    this.sonarCanvas.style.height = `${cssHeight}px`;
    this.sonarCanvas.width = Math.round(cssWidth * dpr);
    this.sonarCanvas.height = Math.round(cssHeight * dpr);

    const ctx = this.sonarContext;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, cssWidth, cssHeight);
    ctx.fillStyle = "#041121";
    ctx.fillRect(0, 0, cssWidth, cssHeight);
    ctx.lineWidth = 1;
    ctx.strokeStyle = "#0d4a72";

    const startX = Math.max(0, Math.floor(this.camera.x / this.level.tileSize) - 1);
    const startY = Math.max(0, Math.floor(this.camera.y / this.level.tileSize) - 1);
    const endX = Math.min(this.level.width, Math.ceil((this.camera.x + cssWidth / this.camera.zoom) / this.level.tileSize) + 1);
    const endY = Math.min(this.level.height, Math.ceil((this.camera.y + cssHeight / this.camera.zoom) / this.level.tileSize) + 1);
    for (let y = startY; y < endY; y += 1) {
      for (let x = startX; x < endX; x += 1) {
        const tile = sonarTileForDisplay(tileAt(this.level, x, y), x, y, this.openedDoors);
        ctx.fillStyle = sonarColorForTile(tile);
        const screenX = (x * this.level.tileSize - this.camera.x) * this.camera.zoom;
        const screenY = (y * this.level.tileSize - this.camera.y) * this.camera.zoom;
        const drawX = Math.floor(screenX);
        const drawY = Math.floor(screenY);
        const drawSize = Math.ceil(cellSize);
        ctx.fillRect(drawX, drawY, drawSize, drawSize);
        if (isWallTile(tile)) {
          ctx.strokeRect(drawX + 0.5, drawY + 0.5, Math.max(0, drawSize - 1), Math.max(0, drawSize - 1));
        }
      }
    }

    this.drawSonarPlayerMarker(ctx, this.playerPos.x, this.playerPos.y, "#49d46c", cellSize);
    this.lastSonarSignature = signature;
  }

  private drawSonarPlayerMarker(
    ctx: CanvasRenderingContext2D,
    worldX: number,
    worldY: number,
    color: string,
    cellSize: number,
  ): void {
    const x = (worldX - this.camera.x) * this.camera.zoom;
    const y = (worldY - this.camera.y) * this.camera.zoom;
    if (x < -cellSize || y < -cellSize || x > this.app.screen.width + cellSize || y > this.app.screen.height + cellSize) return;
    const radius = Math.max(2, cellSize * 0.16);
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "rgba(0, 0, 0, 0.55)";
    ctx.lineWidth = Math.max(1, cellSize * 0.035);
    ctx.stroke();
  }

  private compassTarget(): Vec2 | null {
    const nextKey = this.level.entities.keys
      .filter((entity) => !this.collectedKeyEntities.has(entityId(entity)))
      .sort((a, b) => (numericSuffix(a.tile) ?? 0) - (numericSuffix(b.tile) ?? 0))[0];
    const target = nextKey ?? this.level.entities.finish;
    if (!target) return null;
    return {
      x: (target.x + 0.5) * this.level.tileSize,
      y: (target.y + 0.5) * this.level.tileSize,
    };
  }

  private startMiningFacingWall(slotIndex: number): boolean {
    if (this.miningJob) return false;
    const playerTile = {
      x: Math.floor(this.playerPos.x / this.level.tileSize),
      y: Math.floor(this.playerPos.y / this.level.tileSize),
    };
    const vector = directionVector(this.facing);
    const wallX = playerTile.x + vector.x;
    const wallY = playerTile.y + vector.y;
    const tile = tileAt(this.level, wallX, wallY);
    if (!isMineableWall(tile)) {
      this.updateActionHint();
      return false;
    }
    this.miningJob = {
      x: wallX,
      y: wallY,
      tile,
      slotIndex,
      startedAt: performance.now(),
    };
    this.pickaxeArmedUntil = performance.now() + MINING_ANIMATION_MS;
    this.options.audio?.playMiningLoop(miningSurfaceForTile(tile));
    this.lastInventorySignature = "";
    this.updateHudState();
    return true;
  }

  private finishMining(job: MiningJob): void {
    const replacement = pathTileFor(job.tile);
    this.level.rows[job.y][job.x] = replacement;
    this.minedWalls.add(tileId(job.x, job.y));
    if (this.level.entities.lights.some((light) => light.x === job.x && light.y === job.y)) {
      this.hiddenLights.add(tileId(job.x, job.y));
    }
    this.decrementInventorySlot(job.slotIndex);
    this.options.audio?.stopMiningLoop();
    this.options.audio?.playMiningHit(miningSurfaceForTile(job.tile));
    if (this.inventoryCount("pickaxe") <= 0) {
      this.pickaxeArmedUntil = 0;
    }
    this.miningJob = null;
    this.miningSprite.visible = false;
    this.updateHudState();
    this.drawTiles();
  }

  private updateMining(_deltaSeconds: number): void {
    const job = this.miningJob;
    if (!job) {
      this.miningSprite.visible = false;
      this.options.audio?.stopMiningLoop();
      return;
    }
    const elapsed = performance.now() - job.startedAt;
    const progress = clamp(elapsed / MINING_ANIMATION_MS, 0, 1);
    const frames = this.textures.miningFrames;
    this.miningSprite.texture = frames[Math.min(frames.length - 1, Math.floor(progress * frames.length))] ?? Texture.EMPTY;
    const screen = this.worldToScreen({
      x: (job.x + 0.5) * this.level.tileSize,
      y: (job.y + 0.5) * this.level.tileSize,
    });
    this.miningSprite.x = Math.round(screen.x);
    this.miningSprite.y = Math.round(screen.y);
    this.miningSprite.width = this.level.tileSize * this.camera.zoom;
    this.miningSprite.height = this.level.tileSize * this.camera.zoom;
    this.miningSprite.visible = true;
    if (progress >= 1) {
      this.finishMining(job);
    }
  }

  private updateFps(deltaSeconds: number): void {
    this.fpsElapsed += deltaSeconds;
    this.fpsFrames += 1;
    if (this.fpsElapsed < 0.35) return;
    const fps = this.fpsFrames / this.fpsElapsed;
    if (this.hudFpsElement) {
      this.hudFpsElement.textContent = `${fps.toFixed(0)} FPS`;
    }
    this.fpsElapsed = 0;
    this.fpsFrames = 0;
  }

  private readonly onResize = (): void => {
    this.rebuildTilePool();
  };

  public debugTeleportToTile(x: number, y: number): void {
    this.playerPos.x = (x + 0.5) * this.level.tileSize;
    this.playerPos.y = (y + 0.5) * this.level.tileSize;
    this.checkGameplayInteractions();
    this.updateCamera();
    this.drawTiles();
    this.drawEntities();
    this.drawPlayerAndOverlay();
  }

  public getDebugState() {
    const playerTile = {
      x: Math.floor(this.playerPos.x / this.level.tileSize),
      y: Math.floor(this.playerPos.y / this.level.tileSize),
    };
    return {
      player: {
        x: this.playerPos.x,
        y: this.playerPos.y,
        tileX: playerTile.x,
        tileY: playerTile.y,
      },
      keys: {
        owned: this.ownedKeys.size,
        ownedList: [...this.ownedKeys].sort((a, b) => a - b),
        total: this.level.entities.keys.length,
      },
      fuel: this.fuel,
      coins: this.coins,
      inventory: Object.fromEntries(this.inventoryEntries()),
      inventorySlots: this.inventorySlots.map((slot) => (slot ? { ...slot } : null)),
      powerUps: [...this.activePowerUps].sort(),
      elapsedMs: Math.floor(this.runElapsedMs),
      seed: this.level.seed,
      difficulty: this.level.difficulty.key,
      camera: { ...this.camera },
      activeEffects: {
        range: this.isEffectActive("range"),
        sonar: this.isEffectActive("sonar"),
        compass: this.isEffectActive("compass"),
        pickaxe: this.isEffectActive("pickaxe"),
      },
      minedWalls: {
        total: this.minedWalls.size,
        list: [...this.minedWalls].sort(),
      },
      fuelStations: this.fuelStations.map((station) => ({ x: station.x, y: station.y })),
      doors: {
        opened: this.openedDoors.size,
        openedList: [...this.openedDoors].sort(),
        total: this.level.entities.doors.length,
      },
      won: this.won,
      gameOver: this.gameOver,
      paused: this.paused,
      entities: {
        start: this.level.entities.start,
        finish: this.level.entities.finish,
        keys: this.level.entities.keys.map((entity) => ({
          ...entity,
          number: numericSuffix(entity.tile),
          collected: this.collectedKeyEntities.has(entityId(entity)),
        })),
        doors: this.level.entities.doors.map((entity) => ({
          ...entity,
          opened: this.openedDoors.has(tileId(entity.x, entity.y)),
        })),
        dealers: this.level.entities.dealers,
        items: this.level.entities.items.map((entity) => ({
          ...entity,
          collected: this.collectedItemEntities.has(entityId(entity)),
        })),
        fuelStations: this.fuelStations,
        lights: this.level.entities.lights,
      },
    };
  }

  public debugSetFuel(value: number): void {
    this.fuel = clamp(value, 0, 1);
    this.gameOver = this.fuel <= 0;
    if (this.gameOver) {
      this.options.audio?.playRandomDeathVoiceLine(0.68);
      this.notifyRunEnded("gameOver");
    }
    this.updateLowFuelWarningAudio();
    this.updateHudState();
    this.drawPlayerAndOverlay();
  }

  public debugSetCoins(value: number): void {
    this.coins = Math.max(0, Math.floor(value));
    this.updateHudState();
    if (this.shopOpen) {
      this.renderShop();
    }
  }

  public debugGiveItem(type: string, count = 1): void {
    const normalizedType = normalizeCheatGiveType(type);
    if (normalizedType === "coin" || normalizedType === "gems") {
      this.debugSetCoins(this.coins + count);
      return;
    }
    if (normalizedType === "key") {
      this.level.entities.keys.forEach((entity) => {
        const keyNumber = numericSuffix(entity.tile);
        if (keyNumber !== null) this.ownedKeys.add(keyNumber);
      });
      this.updateHudState();
      return;
    }
    if (normalizedType.startsWith("key")) {
      const keyNumber = Number(normalizedType.replace(/\D/g, ""));
      if (Number.isFinite(keyNumber) && keyNumber > 0) {
        this.ownedKeys.add(keyNumber);
        this.updateHudState();
      }
      return;
    }
    if (isPowerUpType(normalizedType)) {
      this.activatePowerUp(normalizedType);
      this.updateHudState();
      return;
    }
    if (!INVENTORY_ORDER.includes(normalizedType as InventoryItemType)) {
      return;
    }
    this.addInventoryItem(normalizedType, Math.max(1, Math.floor(count)));
    this.updateHudState();
  }

  public debugGiveKey(keyNumber: number): void {
    const normalized = Math.max(1, Math.floor(keyNumber));
    this.ownedKeys.add(normalized);
    this.updateHudState();
  }

  public debugSetNoClip(enabled: boolean): void {
    this.noClip = enabled;
  }

  public debugSetFuelFrozen(enabled: boolean): void {
    this.fuelFrozen = enabled;
  }

  public debugTeleportToRegion(region: number): boolean {
    for (let y = 1; y < this.level.height - 1; y += 1) {
      for (let x = 1; x < this.level.width - 1; x += 1) {
        const tile = tileAt(this.level, x, y);
        if (isSolidTile(tile)) continue;
        if ((numericSuffix(tile) ?? 1) !== region) continue;
        this.debugTeleportToTile(x, y);
        return true;
      }
    }
    return false;
  }

  public debugTeleportToDealer(index = 0): boolean {
    const dealer = this.level.entities.dealers[index];
    if (!dealer) return false;
    this.debugTeleportToTile(dealer.x, dealer.y + 1);
    return true;
  }

  public debugTeleportToStart(): boolean {
    const start = this.level.entities.start;
    if (!start) return false;
    this.debugTeleportToTile(start.x, start.y);
    return true;
  }

  public debugTeleportToEnd(): boolean {
    const finish = this.level.entities.finish;
    if (!finish) return false;
    this.debugTeleportToTile(finish.x, finish.y);
    return true;
  }

  public debugLevelReport() {
    const report: Record<string, { cells: number; walls: number; innerWalls: number; floors: number }> = {};
    for (const row of this.level.rows) {
      for (const tile of row) {
        const region = String(numericSuffix(tile) ?? 1);
        report[region] ??= { cells: 0, walls: 0, innerWalls: 0, floors: 0 };
        report[region].cells += 1;
        if (isWallTile(tile)) report[region].walls += 1;
        if (tile.startsWith("W_")) report[region].innerWalls += 1;
        if (!isSolidTile(tile)) report[region].floors += 1;
      }
    }
    return report;
  }

  public debugTeleportNextToMineableWall() {
    const directions = [
      { facing: "right" as PlayerDirection, adjacent: { x: -1, y: 0 } },
      { facing: "left" as PlayerDirection, adjacent: { x: 1, y: 0 } },
      { facing: "down" as PlayerDirection, adjacent: { x: 0, y: -1 } },
      { facing: "up" as PlayerDirection, adjacent: { x: 0, y: 1 } },
    ];
    for (let y = 1; y < this.level.height - 1; y += 1) {
      for (let x = 1; x < this.level.width - 1; x += 1) {
        if (!isMineableWall(tileAt(this.level, x, y))) continue;
        for (const direction of directions) {
          const playerX = x + direction.adjacent.x;
          const playerY = y + direction.adjacent.y;
          const playerTile = tileAt(this.level, playerX, playerY);
          if (isSolidTile(playerTile)) continue;
          this.facing = direction.facing;
          this.debugTeleportToTile(playerX, playerY);
          return {
            player: { x: playerX, y: playerY },
            wall: { x, y, tile: tileAt(this.level, x, y) },
            facing: direction.facing,
          };
        }
      }
    }
    return null;
  }

  public setPaused(paused: boolean): void {
    if (this.won || this.gameOver) return;
    if (paused && this.shopOpen) {
      this.closeShop();
    }
    if (paused && this.easterEggOpen) {
      this.closeEasterEgg(false);
    }
    this.paused = paused;
    this.updateLowFuelWarningAudio();
    this.updateHudState();
  }

  public isPaused(): boolean {
    return this.paused;
  }

  public resume(): void {
    this.setPaused(false);
  }

  public getLightMaskSample() {
    const screen = this.screenSize();
    const dpr = Math.max(1, Math.min(window.devicePixelRatio || 1, 2));
    const centerX = Math.round(this.player.x * dpr);
    const centerY = Math.round(this.player.y * dpr);
    const corners = [
      { x: 8, y: 8 },
      { x: Math.max(8, screen.width - 8), y: 8 },
      { x: 8, y: Math.max(8, screen.height - 8) },
      { x: Math.max(8, screen.width - 8), y: Math.max(8, screen.height - 8) },
    ].sort((a, b) => distance(b, this.player) - distance(a, this.player));
    const center = this.lightContext.getImageData(centerX, centerY, 1, 1).data;
    const outsideAlphas = corners.map((corner) => {
      const x = Math.round(corner.x * dpr);
      const y = Math.round(corner.y * dpr);
      return this.lightContext.getImageData(x, y, 1, 1).data[3];
    });
    return {
      centerAlpha: center[3],
      outsideAlpha: Math.max(...outsideAlphas),
      canvasWidth: this.lightCanvas.width,
      canvasHeight: this.lightCanvas.height,
      cssWidth: screen.width,
      cssHeight: screen.height,
    };
  }

  private updatePlayerAnimation(deltaSeconds: number, moving: boolean): void {
    const nextState: PlayerAnimationState = moving ? "moving" : "idle";
    if (nextState !== this.playerAnimationState) {
      this.playerAnimationState = nextState;
      this.playerAnimationFrame = 0;
      this.playerAnimationElapsed = 0;
    }
    const frames = this.currentPlayerFrames();
    if (frames.length === 0) return;
    this.playerAnimationElapsed += deltaSeconds;
    const fps = this.playerAnimationState === "idle" ? PLAYER_IDLE_ANIMATION_FPS : PLAYER_ANIMATION_FPS;
    const frameDuration = 1 / fps;
    while (this.playerAnimationElapsed >= frameDuration) {
      this.playerAnimationElapsed -= frameDuration;
      this.playerAnimationFrame = (this.playerAnimationFrame + 1) % frames.length;
    }
    this.player.texture = this.currentPlayerTexture();
  }

  private currentPlayerFrames(): Texture[] {
    return this.textures.playerAnimations[this.facing][this.playerAnimationState] || [this.textures.player];
  }

  private currentPlayerTexture(): Texture {
    const frames = this.currentPlayerFrames();
    return frames[this.playerAnimationFrame % frames.length] ?? this.textures.player;
  }

  private updateLowFuelWarningAudio(): void {
    const active = !this.paused && !this.shopOpen && !this.won && !this.gameOver;
    this.options.audio?.updateLowFuelWarning(this.fuel, LOW_FUEL_WARNING, active);
  }

  private notifyRunEnded(status: RunEndStatus): void {
    this.options.audio?.stopLowFuelWarning();
    if (this.runEndNotified) return;
    this.runEndNotified = true;
    this.options.onRunEnded?.(status, this.getDebugState());
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function lerp(start: number, end: number, t: number): number {
  return start + (end - start) * clamp(t, 0, 1);
}

function distance(a: Vec2, b: Vec2): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function wallBleedSamplePoint(
  side: "left" | "right" | "top" | "bottom",
  tileLeft: number,
  tileTop: number,
  tileSize: number,
): Vec2 {
  const quarter = tileSize * 0.25;
  const centerX = tileLeft + tileSize * 0.5;
  const centerY = tileTop + tileSize * 0.5;
  if (side === "left") {
    return { x: tileLeft + quarter, y: centerY };
  }
  if (side === "right") {
    return { x: tileLeft + tileSize - quarter, y: centerY };
  }
  if (side === "top") {
    return { x: centerX, y: tileTop + quarter };
  }
  return { x: centerX, y: tileTop + tileSize - quarter };
}

function cornerPoint(corner: "tl" | "tr" | "bl" | "br", tileLeft: number, tileTop: number, tileSize: number): Vec2 {
  if (corner === "tl") return { x: tileLeft, y: tileTop };
  if (corner === "tr") return { x: tileLeft + tileSize, y: tileTop };
  if (corner === "bl") return { x: tileLeft, y: tileTop + tileSize };
  return { x: tileLeft + tileSize, y: tileTop + tileSize };
}

function numericSuffix(value: string): number | null {
  const match = value.match(/\d+/);
  if (!match) return null;
  const number = Number(match[0]);
  return Number.isFinite(number) ? number : null;
}

function requiredKeyForDoor(tile: string): number | null {
  const doorNumber = numericSuffix(tile);
  return doorNumber === null ? null : doorNumber + 1;
}

function musicForRegion(region: number): MusicKey {
  if (region === 1) return "level1";
  if (region === 2) return "dungeon";
  if (region === 3) return "moonlight";
  if (region === 4) return "horns";
  return "dungeon";
}

function resolveAmbientRegion(level: LevelData, tileX: number, tileY: number, lastRegion: number | null): number | null {
  const direct = reliableRegionFromTile(tileAt(level, tileX, tileY));
  if (direct !== null) return direct;

  const counts = new Map<number, number>();
  for (let dy = -1; dy <= 1; dy += 1) {
    for (let dx = -1; dx <= 1; dx += 1) {
      if (dx === 0 && dy === 0) continue;
      const neighborRegion = reliableRegionFromTile(tileAt(level, tileX + dx, tileY + dy));
      if (neighborRegion === null) continue;
      counts.set(neighborRegion, (counts.get(neighborRegion) ?? 0) + 1);
    }
  }
  if (counts.size === 0) return lastRegion;

  let bestRegion: number | null = null;
  let bestCount = -1;
  for (const [region, count] of counts) {
    if (count > bestCount) {
      bestRegion = region;
      bestCount = count;
      continue;
    }
    if (count === bestCount && lastRegion !== null && region === lastRegion) {
      bestRegion = region;
      bestCount = count;
    }
  }
  if (bestRegion === null) return lastRegion;
  if (lastRegion !== null && counts.get(lastRegion) === bestCount) return lastRegion;
  return bestRegion;
}

function reliableRegionFromTile(tile: string): number | null {
  const normalized = tile.toUpperCase();
  if (normalized === "S_1") return 1;
  if (normalized.startsWith("E_")) return numericSuffix(normalized);
  if (
    normalized.startsWith("P_") ||
    normalized.startsWith("K_") ||
    normalized.startsWith("W_") ||
    normalized.startsWith("WW") ||
    normalized.startsWith("KW") ||
    normalized.startsWith("M_")
  ) {
    return numericSuffix(normalized);
  }
  return null;
}

function isDealerHouseTile(tile: string): boolean {
  const normalized = tile.toUpperCase();
  return normalized.startsWith("K_") || normalized.startsWith("D_");
}

function sonarTileForDisplay(tile: string, tileX: number, tileY: number, openedDoors: Set<string>): string {
  if (!tile.startsWith("T")) return tile;
  if (openedDoors.has(tileId(tileX, tileY))) {
    return `P_${Math.max(1, numericSuffix(tile) ?? 1)}`;
  }
  return `W_${Math.max(1, numericSuffix(tile) ?? 1)}`;
}

function itemDisplaySize(itemType: string | undefined): { width: number; height: number } {
  if (itemType === "coin") return { width: 32, height: 32 };
  if (itemType === "range") return { width: 32, height: 32 };
  if (itemType === "sonar") return { width: 32, height: 32 };
  if (itemType === "pickaxe") return { width: 42, height: 42 };
  if (itemType === "fuelStation") return { width: 35, height: 35 };
  return { width: 32, height: 31 };
}

function sonarColorForTile(tile: string): string {
  if (tile.startsWith("M_")) return "#163e5a";
  if (tile === "WWW") return "#89d2f5";
  if (isWallTile(tile)) return "#9adfff";
  if (tile.startsWith("E_")) return "#f4e16a";
  if (tile === "S_1") return "#63c86d";
  if (tile.startsWith("T")) return "#6bc0ec";
  return "#030910";
}

function sonarColorForItem(itemType: string | undefined): string {
  if (itemType === "coin") return "#4fe985";
  if (itemType === "fuel") return "#e95b2f";
  if (itemType === "sonar") return "#61a9ff";
  if (itemType === "range") return "#d0a2ff";
  if (itemType === "fuelStation") return "#f06b3b";
  if (itemType === "pickaxe") return "#caa36b";
  return "#f5e6a7";
}

function keyColorForTile(tile: string): string {
  const colors: Record<number, string> = {
    2: "#d8f2ff",
    3: "#7ee37a",
    4: "#f3d34a",
    5: "#df6fe8",
    6: "#f47a54",
  };
  return colors[numericSuffix(tile) ?? 2] ?? "#fff4a8";
}

function doorColorForTile(tile: string): string {
  const required = requiredKeyForDoor(tile) ?? 2;
  return keyColorForTile(`KY${required}`);
}

function doorRotation(tile: string): number {
  const orientation = tile.slice(0, 2).toUpperCase();
  if (orientation === "TL") return 0;
  if (orientation === "TR") return Math.PI;
  if (orientation === "TU") return Math.PI / 2;
  if (orientation === "TD") return -Math.PI / 2;
  return 0;
}

function formatElapsed(elapsedMs: number): string {
  const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const tenth = Math.floor((elapsedMs % 1000) / 100);
  return `${minutes}:${seconds.toString().padStart(2, "0")}.${tenth}`;
}

function inventoryIconUrl(type: string): string {
  if (type === "range") return uiIconUrls.range;
  if (type === "sonar") return uiIconUrls.sonar;
  if (type === "pickaxe") return uiIconUrls.pickaxe;
  if (type === "compass") return uiIconUrls.compass;
  if (type === "betterTank") return uiIconUrls.betterTank;
  if (type === "advancedView") return uiIconUrls.advancedView;
  if (type === "gemMagnet") return uiIconUrls.gemMagnet;
  if (type === "easterEgg") return uiIconUrls.easterEgg;
  if (type === "fuelStation") return uiIconUrls.fuelStation;
  if (type === "coin") return uiIconUrls.coin;
  return uiIconUrls.fuel;
}

function directionVector(direction: PlayerDirection): Vec2 {
  if (direction === "up") return { x: 0, y: -1 };
  if (direction === "down") return { x: 0, y: 1 };
  if (direction === "left") return { x: -1, y: 0 };
  return { x: 1, y: 0 };
}

function isMineableWall(tile: string): boolean {
  return tile.startsWith("W_");
}

function pathTileFor(tile: string): string {
  const region = numericSuffix(tile) ?? 1;
  return `M_${region}`;
}

function miningSurfaceForTile(tile: string): MiningSurface {
  if (tile.endsWith("3")) return "nether";
  if (tile.endsWith("2")) return "stone";
  return "wood";
}

function isPowerUpType(type: string): type is PowerUpType {
  return type === "betterTank" || type === "advancedView" || type === "gemMagnet";
}

function normalizeCheatGiveType(rawType: string): string {
  const normalized = rawType.trim().toLowerCase();
  const aliases: Record<string, string> = {
    coin: "coin",
    coins: "coin",
    gem: "gems",
    gems: "gems",
    key: "key",
    fuelstation: "fuelStation",
    fuel_station: "fuelStation",
    station: "fuelStation",
    campfire: "fuelStation",
    camp_fire: "fuelStation",
    camp: "fuelStation",
    tank: "betterTank",
    bettertank: "betterTank",
    advancedview: "advancedView",
    magnet: "gemMagnet",
    gemmagnet: "gemMagnet",
  };
  return aliases[normalized] ?? rawType.trim();
}

function inventorySlotIndexFromCode(code: string): number | null {
  if (code.startsWith("Digit")) {
    const digit = Number(code.slice("Digit".length));
    return digit >= 1 && digit <= 6 ? digit - 1 : null;
  }
  if (code.startsWith("Numpad")) {
    const digit = Number(code.slice("Numpad".length));
    return digit >= 1 && digit <= 6 ? digit - 1 : null;
  }
  return null;
}

function entityId(entity: LevelEntity): string {
  return `${entity.x},${entity.y},${entity.tile}`;
}

function tileId(x: number, y: number): string {
  return `${x},${y}`;
}

function easeSmooth(value: number): number {
  return value * value * (3 - 2 * value);
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    target.isContentEditable
  );
}
